import { useEffect, useRef, useState } from 'react';
import {
  api, darf, datum, datumKurz, kwSpanne, menge, monat, zeitpunkt, GEWERK_TEXT,
  ZUSTAND_TEXT, type Anmelder, tagIso} from '../api.ts';
import { Kopf, Karte, Kachel, Leer } from '../teile/Geruest.tsx';
import { spanneText } from '../teile/Positionsauswertung.tsx';
import { Positionsauswertung } from '../teile/Positionsauswertung.tsx';
import { GeldJeAuftrag } from '../teile/GeldJeAuftrag.tsx';
import {
  Dialog, Fehlerkasten, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import {
  heuteTag, jahrVon, montagVon, plusTage, type Tag,
} from '../teile/rastertage.ts';

/** Balken ohne Bibliothek. Eine Zahl mit einem Streifen davor liest sich
 *  schneller als eine Zahl allein.
 *
 *  NULL BEKOMMT KEINEN STUMMEL. Die Mindestbreite von zwei Prozent gibt es,
 *  damit eine kleine Zahl nicht unsichtbar wird; sie galt aber auch für die
 *  Null, und dann stand neben einer 0 ein Streifen. Das sieht aus wie „ein
 *  bisschen", und ein bisschen ist etwas anderes als nichts. */
function Balken({ wert, von, farbe }: { wert: number; von: number; farbe?: string }) {
  const b = wert > 0 && von > 0 ? Math.max(2, Math.round((wert / von) * 100)) : 0;
  return (
    <div className="balken">
      <div style={{ width: b + '%', background: farbe ?? 'var(--akzent)' }} />
    </div>
  );
}

/* ZWEI SORTEN ZAHL, UND SIE SEHEN GLEICH AUS.
 *
 * Eine Bestandszahl beantwortet „wie steht es JETZT": 17 überfällige
 * Aufträge sind heute überfällig, egal welche zwölf Monate eingestellt sind.
 * Eine Zeitraumzahl beantwortet „was ist DARIN passiert": 340 erledigte
 * Gewerke in diesen zwölf Monaten.
 *
 * Nebeneinander in derselben Kachelreihe wären sie eine Falle, die niemandem
 * auffällt: man stellt drei Monate ein, die Bestandszahl bleibt stehen, und
 * ab da glaubt man, sie hätte sich auf drei Monate bezogen. Deshalb trägt
 * jede Karte und jede Spalte ihre Sorte im Kopf.
 */
function Sorte({ art }: { art: 'zeitraum' | 'bestand' }) {
  return (
    <span className={'sorte ' + art}>
      {art === 'zeitraum' ? 'im Zeitraum' : 'Bestand heute'}
    </span>
  );
}

/** Dieselbe Angabe über einer Zahlenspalte, klein und ohne Fläche. */
function Sortewort({ art }: { art: 'zeitraum' | 'bestand' }) {
  return (
    <span className="sortewort">
      {art === 'zeitraum' ? 'im Zeitraum' : 'heute'}
    </span>
  );
}

/** Ein Datum um n Monate zurück, als ISO. */
function vorMonaten(bis: string, n: number) {
  const d = new Date(bis + 'T12:00:00Z');
  d.setUTCMonth(d.getUTCMonth() - n);
  return d.toISOString().slice(0, 10);
}

/** Ein Datum um n Tage weiter, als ISO. Für „diese Woche" braucht es den
 *  Sonntag, und der ist der Montag plus sechs.
 *
 *  IN UTC GERECHNET UND ALS TAG ZURÜCK. Ein blanker Tag hat keine Zeitzone;
 *  wer ihn über die lokale Mitternacht schiebt, verliert westlich von
 *  Greenwich einen Tag. Dieselbe Falle wie überall in diesem Haus. */
function tageSpaeter(iso: string, n: number) {
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

type Frage = { titel: string; w: Record<string, string | undefined> };

/* Die fünf baubaren Stände, in der Reihenfolge, in der die Arbeit liegt.
   Wortgleich zu `v_auftrag_baustand` (Migration 071) - die Liste steht hier
   nur, um die Wörter für Menschen zu tragen und die Reihenfolge zu halten. */
const BAUSTAND: [string, string, string | undefined][] = [
  ['spleisstermin_ueberfaellig', 'Spleißtermin überfällig', 'var(--ueberfaellig)'],
  ['terminiert',                 'Terminiert', undefined],
  ['tiefbau_terminiert',         'Tiefbau terminiert', undefined],
  ['sofort_spleissbar',          'Sofort spleißbar', 'var(--erledigt)'],
  ['wartet_auf_tiefbau',         'Wartet auf Tiefbau', 'var(--neu)'],
];

const NICHT_BAUBAR: [string, string][] = [
  ['wartet',        'Wartet'],
  ['unterbrochen',  'Unterbrochen'],
  ['abgeschlossen', 'Abgeschlossen'],
  ['archiviert',    'Archiviert'],
];

/** Eine Anschrift aus den Einzelteilen, wie sie überall sonst steht. */
function anschrift(x: any): string {
  return `${x.strasse} ${x.hausnummer}${x.hausnummer_zusatz ?? ''}, `
       + `${x.plz} ${x.ort_text}`;
}

/**
 * Der Wochenbericht.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „das gehört in die auswerung rein. später verfeinern wir sie."
 *
 * JEDE ZAHL SAGT, WORAUF SIE SICH BEZIEHT. Der Bestand kennt keinen
 * Zeitraum, der Rückblick ist vier Wochen, der Zeitraum ist der eingestellte
 * - drei verschiedene Bezüge auf einem Bildschirm, und wer sie verwechselt,
 * plant falsch.
 */
/**
 * EIN BALKENDIAGRAMM, KEINE TABELLE MIT STREIFEN.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „außerdem die bauleistungen mal in einem balkendiagram darstellen."
 *
 * Die Streifen, die dieses System bisher kennt, liegen WAAGERECHT in einer
 * Tabellenzeile und vergleichen Zeilen miteinander (welche Firma, welche
 * Gemeinde). Eine LEISTUNG über die Zeit liest man anders: senkrecht,
 * nebeneinander, mit der Zeit von links nach rechts. Erst dann sieht man den
 * Einbruch in der Urlaubswoche und den Anstieg danach - und genau darum geht
 * es bei einer Bauleistung.
 *
 * KEINE BIBLIOTHEK. Ein Balken ist ein `div` mit einer Höhe in Prozent; das
 * sind zwanzig Zeilen CSS. Eine Diagrammbibliothek wäre 200 kB für ein
 * Rechteck, und sie brächte ihre eigene Schrift und ihre eigenen Farben mit -
 * gegen die Regel, dass Farben NUR in `stil.css` stehen.
 *
 * GESTAPELT NACH GEWERK, wo es eines gibt: Tiefbau, Einblasen, Spleißen
 * übereinander in einem Balken. Wer nur die Summe zeigt, sieht nicht, dass
 * der Tiefbau schon läuft, während die Montage noch steht.
 */
function Wochenbalken(
  { zeilen, titel, klick, gestapelt }:
  { zeilen: any[]; titel: string; klick?: (z: any) => void; gestapelt?: boolean }
) {
  const wert = (z: any) => gestapelt
    ? Number(z.tiefbau ?? 0) + Number(z.einblasen ?? 0) + Number(z.spleissen ?? 0)
    : Number(z.n ?? 0);
  const hoechst = Math.max(...zeilen.map(wert), 1);

  /* DIE NEUESTE WOCHE STEHT IM BILD (.358). Marvin, 14.10.2026, auf dem
     Handy: „warum ist das Diagramm nicht aktuell? Es hat im August einfach
     aufgehört." Die Reihe rollt seitlich und stand am LINKEN Ende - die
     ältesten Wochen sichtbar, die aktuellen rechts ausserhalb. Jetzt
     springt sie beim Laden ans rechte Ende; wer zurückblättern will,
     wischt nach links. */
  const rolle = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = rolle.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [zeilen.length]);

  return (
    <div className="balkenwerk">
      <div className="klein leise balkenwerk-titel">{titel}</div>
      <div className="rollt" ref={rolle}>
        <div className="balkenreihe">
          {zeilen.map((z) => {
            const gesamt = wert(z);
            const hoch = (n: number) => `${(n / hoechst) * 100}%`;
            const inhalt = (
              <>
                {/* DIE ZAHL STEHT ÜBER DEM BALKEN, nicht darin: bei einer 1
                    ist der Balken drei Pixel hoch und die Zahl darin
                    unsichtbar. */}
                <span className="balkenzahl">{gesamt || ''}</span>
                {/* DAS FELD HAT EINE FESTE HÖHE, DIE SÄULE EINEN ANTEIL DAVON.
                    Ohne diese Hülle rechnet der Browser die Prozentangabe
                    gegen einen Kasten, der selbst nur so hoch ist wie sein
                    Inhalt - und der Balken bleibt unsichtbar. Beim ersten
                    Anlauf stand genau das auf dem Bildschirm: eine Zahl über
                    einer Linie. */}
                <span className="balkenfeld">
                <span className="balkensaeule" style={{ height: hoch(gesamt) }}>
                  {gestapelt && (
                    <>
                      <span className="teil sp"
                            style={{ height: `${(Number(z.spleissen ?? 0) / (gesamt || 1)) * 100}%` }}
                            title={`Spleißen: ${z.spleissen ?? 0}`} />
                      <span className="teil eb"
                            style={{ height: `${(Number(z.einblasen ?? 0) / (gesamt || 1)) * 100}%` }}
                            title={`Einblasen: ${z.einblasen ?? 0}`} />
                      <span className="teil tb"
                            style={{ height: `${(Number(z.tiefbau ?? 0) / (gesamt || 1)) * 100}%` }}
                            title={`Tiefbau: ${z.tiefbau ?? 0}`} />
                    </>
                  )}
                </span>
                </span>
                <span className="balkenfuss">
                  KW {z.kw}
                  <span className="leise">{datumKurz(z.montag)}</span>
                </span>
              </>
            );
            return klick
              ? <button key={z.montag} className="balken-eins" onClick={() => klick(z)}
                        title={`KW ${z.kw}/${z.jahr} ab ${datumKurz(z.montag)}`}>
                  {inhalt}
                </button>
              : <div key={z.montag} className="balken-eins">{inhalt}</div>;
          })}
        </div>
      </div>
      {gestapelt && (
        <div className="klein leise balkenlegende">
          <span><i className="punkt tb" /> Tiefbau</span>
          <span><i className="punkt eb" /> Einblasen</span>
          <span><i className="punkt sp" /> Spleißen</span>
        </div>
      )}
    </div>
  );
}


function Bericht({ b, setFrage }: { b: any; setFrage: (f: Frage) => void }) {
  /* DIE VIER ANKERTAGE FÜR DIE ABRECHNUNGSKACHELN.
   *
   * Sie werden HIER gerechnet und nicht aus dem eingestellten Zeitraum
   * genommen: „heute", „diese Woche", „dieser Monat" und „dieses Jahr" sind
   * Bestandszahlen und hängen ausdrücklich NICHT am Filter oben - genau so
   * zählt sie der Server. Stünde hinter der Kachel eine andere Spanne als in
   * ihr, wäre die Liste eine andere als die Zahl.
   *
   * In ORTSZEIT, nie über `toISOString()` auf einem Datum mit Uhrzeit: das
   * rechnet nach UTC um und macht abends aus dem Montag den Sonntag. */
  /* `tagIso` STAND HIER ZWEIMAL IN DERSELBEN DATEI und las beide Male die
     Uhr des Betrachters. Sie kommt jetzt aus `api.ts`. */
  /* UND SEIT DEM 10.09.2026 OHNE JEDE UHR. Die Monats- und Jahresanfaenge
     entstanden aus `new Date(jahr, monat, 1)`, also auf der Uhr des
     BETRACHTERS, und wurden danach in BERLIN gelesen: in Zypern war der
     „Monatsanfang" der Letzte des Vormonats. */
  const heute = heuteTag();
  const wochenAnfang = montagVon(heute);
  const monatsAnfang = `${heute.slice(0, 7)}-01`;
  const jahresAnfang = `${heute.slice(0, 4)}-01-01`;

  const stand = (k: string) =>
    Number((b.baustand ?? []).find((z: any) => z.stand === k)?.n ?? 0);
  const baubar = BAUSTAND.reduce((n, [k]) => n + stand(k), 0);
  const bestand = (b.baustand ?? []).reduce(
    (n: number, z: any) => n + Number(z.n), 0);
  const p = b.prognose ?? {};

  return (
    <>
      <Karte titel="Baubarer Bestand" rechts={<Sorte art="bestand" />}>
        {/* DIE FÜNF ZAHLEN ERGEBEN ZUSAMMEN „baubar gesamt", und das steht
            dabei. Eine Aufteilung, deren Teile sich nicht zur Summe fügen,
            wird beim dritten Mal nicht mehr geglaubt - die Sicht hält es mit
            einer Gegenprobe fest, hier steht es für den Leser. */}
        <div className="kachelreihe">
          <Kachel wert={menge(baubar)} was="baubar gesamt"
                  klick={() => setFrage({
                    titel: 'Baubar', w: { was: 'baubar' } })} />
          {BAUSTAND.map(([k, wort, farbe]) => (
            <Kachel key={k} wert={menge(stand(k))} was={wort} farbe={farbe}
                    klick={() => setFrage({
                      titel: wort, w: { was: 'baustand', wert: k } })} />
          ))}
        </div>
        <p className="klein leise" style={{ margin: '10px 0 0' }}>
          <b>Baubar</b> heißt: nichts hält ihn auf - nicht abgeschlossen,
          nicht unterbrochen, nicht wartend. Jeder Auftrag steht in genau
          einem der 5 Stände, und zusammen ergeben sie die erste Zahl.
        </p>
        <div className="kachelreihe" style={{ marginTop: 12 }}>
          {NICHT_BAUBAR.map(([k, wort]) => (
            <Kachel key={k} wert={menge(stand(k))} was={wort}
                    klick={() => setFrage({
                      titel: wort, w: { was: 'baustand', wert: k } })} />
          ))}
          <Kachel wert={menge(bestand)} was="Bestand gesamt" />
        </div>
      </Karte>

      <div className="nebeneinander">
        <Karte titel="Wann wir durch sind" rechts={<Sorte art="bestand" />}>
          <table className="liste">
            <tbody>
              <tr style={{ cursor: 'default' }}>
                <td>Terminiert</td>
                <td className="zahl">{menge(p.terminiert)}</td>
              </tr>
              <tr style={{ cursor: 'default' }}>
                <td>Fest verplant bis</td>
                <td className="zahl">
                  {p.festVerplantBis ? datum(p.festVerplantBis)
                    : <span className="leise">kein Termin vergeben</span>}
                </td>
              </tr>
              <tr style={{ cursor: 'default' }}>
                <td>Baubarer Rest ohne Termin</td>
                <td className="zahl"><b>{menge(p.rest)}</b></td>
              </tr>
              <tr style={{ cursor: 'default' }}>
                <td>Tiefbau je Woche</td>
                <td className="zahl">{menge(p.tiefbauProWoche)}</td>
              </tr>
              <tr style={{ cursor: 'default' }}>
                <td>Montage je Woche</td>
                <td className="zahl">{menge(p.montageProWoche)}</td>
              </tr>
              {/* DER ENGPASS IST DIE WICHTIGSTE ZEILE DES ABSCHNITTS. Ein
                  Anschluss ist fertig, wenn GESPLEISST ist; solange die
                  Montage langsamer ist als der Tiefbau, hilft ein zusätzlicher
                  Bagger nichts. */}
              <tr style={{ cursor: 'default' }}>
                <td>Engpass</td>
                <td className="zahl">
                  {p.engpass
                    ? <b>{p.engpass}</b>
                    : <span className="leise">nicht zu sagen</span>}
                </td>
              </tr>
              <tr style={{ cursor: 'default' }}>
                <td>Rest fertig bis</td>
                <td className="zahl">
                  {p.restFertigBis
                    ? <b>{datum(p.restFertigBis)}</b>
                    : <span className="leise">-</span>}
                </td>
              </tr>
              <tr style={{ cursor: 'default' }}>
                <td>Rest-Bauleistung</td>
                <td className="zahl">
                  {p.restArbeitstage === null || p.restArbeitstage === undefined
                    ? <span className="leise">-</span>
                    : `${menge(p.restArbeitstage)} Arbeitstage`}
                </td>
              </tr>
            </tbody>
          </table>
          {/* EINE PROGNOSE AUS EINER NULL IST SCHLIMMER ALS KEINE PROGNOSE -
              sie sieht aus wie eine Auskunft. Wo keine gerechnet werden
              konnte, steht der Grund. */}
          {p.grund && <div className="hinweis klein">{p.grund}</div>}
          <p className="klein leise" style={{ marginBottom: 0 }}>
            Gerechnet mit dem, was in den letzten {b.rueckblick?.wochen} Wochen
            wirklich geschafft wurde ({datum(b.rueckblick?.von)} bis
            {' '}{datum(b.rueckblick?.bis)}) - nicht mit dem, was zugesagt ist.
            Der Rest kommt NACH dem spätesten vergebenen Termin: was terminiert
            ist, ist verplant.
          </p>
        </Karte>

        <Karte titel="Termine im Zeitraum" rechts={<Sorte art="zeitraum" />}>
          {(b.jeGewerkTermin ?? []).length === 0 && (
            <Leer text="In diesem Zeitraum liegt kein Termin." />
          )}
          {(b.jeGewerkTermin ?? []).length > 0 && (
            <table className="liste">
              <thead>
                <tr><th>Gewerk</th>
                    <th className="zahl">geplant</th>
                    <th className="zahl">erledigt</th>
                    <th className="zahl">offen</th>
                    <th className="zahl">Quote</th></tr>
              </thead>
              <tbody>
                {(b.jeGewerkTermin ?? []).map((z: any) => {
                  const offen = Number(z.geplant) - Number(z.erledigt);
                  const quote = Number(z.geplant) > 0
                    ? Math.round(Number(z.erledigt) / Number(z.geplant) * 100)
                    : null;
                  return (
                    <tr key={z.gewerk} style={{ cursor: 'default' }}>
                      <td>{GEWERK_TEXT[z.gewerk] ?? z.gewerk}</td>
                      <td className="zahl">{menge(z.geplant)}</td>
                      <td className="zahl">{menge(z.erledigt)}</td>
                      <td className="zahl">
                        {offen > 0 ? <b className="rot">{menge(offen)}</b> : 0}
                      </td>
                      <td className="zahl">
                        {quote === null ? '-' : quote + ' %'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          <p className="klein leise" style={{ marginBottom: 0 }}>
            Gezählt werden Termine, die IN DIESEM ZEITRAUM lagen - nicht, was
            darin erledigt wurde. Die Quote sagt, wie viel davon gehalten hat.
          </p>
        </Karte>
      </div>

      {/* DIE BAULEISTUNG ÜBER DIE ZEIT. Sie steht VOR den Firmenzahlen: erst
          die Frage „läuft es", dann die Frage „bei wem". */}
      {(b.leistungWochen ?? []).length > 0 && (
        <Karte titel="Bauleistung je Woche" rechts={<Sorte art="bestand" />}>
          <Wochenbalken zeilen={b.leistungWochen} gestapelt
                        titel="Erledigte Gewerke, die letzten 12 Wochen"
                        klick={(w: any) => setFrage({
                          titel: `KW ${w.kw}/${w.jahr}: erledigte Gewerke`,
                          w: { was: 'erledigt', von: w.montag,
                               bis: tageSpaeter(w.montag, 6) } })} />
          <p className="klein leise" style={{ marginBottom: 0 }}>
            Gezählt wird das erledigte GEWERK, nicht der Auftrag: ein Haus
            bringt Tiefbau, Einblasen und Spleißen. Die Dokumentation zählt
            nicht mit, sie ist keine Bauleistung. Ein Klick öffnet die
            Aufträge dieser Woche.
          </p>
        </Karte>
      )}

      <div className="nebeneinander">
        <Karte titel="Was eine Firma schafft" rechts={<Sorte art="zeitraum" />}>
          {(b.firmen ?? []).length === 0 && (
            <Leer text="Im Rückblick hat keine Firma etwas abgeschlossen." />
          )}
          {(b.firmen ?? []).length > 0 && (
            <div className="rollt">
              <table className="liste">
                <thead>
                  <tr><th>Firma</th>
                      <th className="zahl">Tiefbau</th>
                      <th className="zahl">Montage</th>
                      <th className="zahl">freie Tage</th>
                      <th className="zahl">pro Tag</th></tr>
                </thead>
                <tbody>
                  {(b.firmen ?? []).map((f: any) => (
                    <tr key={f.id} style={{ cursor: 'default' }}>
                      <td>
                        {f.firma}
                        {!f.aktiv && (
                          <span className="merker warnung">inaktiv</span>
                        )}
                      </td>
                      <td className="zahl">{menge(f.tiefbau)}</td>
                      <td className="zahl">{menge(f.montage)}</td>
                      <td className="zahl">{menge(f.verf_tage)}</td>
                      <td className="zahl">
                        {/* „NICHT GEMELDET" IST NICHT „SCHAFFT NICHTS". Eine
                            Null stünde hier wie ein Urteil über die Firma,
                            und sie wäre eines über den Kalender. */}
                        {f.pro_tag === null
                          ? <span className="leise">nicht gemeldet</span>
                          : menge(f.pro_tag)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="klein leise" style={{ marginBottom: 0 }}>
            Abschlüsse der letzten {b.rueckblick?.wochen} Wochen, daneben die
            Tage, an denen die Firma gemeldet hat, dass sie da ist. „pro Tag"
            ist der Quotient - die Zahl, mit der sich planen lässt.
          </p>
        </Karte>

        <Karte titel="Wie lange es dauert" rechts={<Sorte art="bestand" />}>
          <div className="kachelreihe">
            <Kachel wert={b.durchlauf?.schnitt === null
                            ? '-' : menge(b.durchlauf?.schnitt)}
                    was="Tage im Schnitt" />
            <Kachel wert={menge(b.durchlauf?.n)} was="abgeschlossene Aufträge" />
          </div>
          <p className="klein leise" style={{ margin: '8px 0' }}>
            Vom BAFA-Eingang bis zur Abrechnung (letztes LEB, nichts mehr offen).
          </p>
          {(b.laengste ?? []).length > 0 && (
            <>
              <div className="klein" style={{ fontWeight: 600, marginTop: 8 }}>
                Die längsten
              </div>
              <table className="liste">
                <tbody>
                  {(b.laengste ?? []).map((x: any) => (
                    <tr key={x.auftrag_id} style={{ cursor: 'default' }}>
                      <td>{anschrift(x)}</td>
                      <td className="zahl">{menge(x.tage)} Tage</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="klein" style={{ fontWeight: 600, marginTop: 10 }}>
                Die schnellsten
              </div>
              <table className="liste">
                <tbody>
                  {(b.schnellste ?? []).map((x: any) => (
                    <tr key={x.auftrag_id} style={{ cursor: 'default' }}>
                      <td>{anschrift(x)}</td>
                      <td className="zahl">{menge(x.tage)} Tage</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {(b.liegezeit ?? []).length > 0 && (
            <>
              <div className="klein" style={{ fontWeight: 600, marginTop: 12 }}>
                Liegezeit je Gewerk
              </div>
              <table className="liste">
                <thead>
                  <tr><th>Gewerk</th>
                      <th className="zahl">Tage im Schnitt</th>
                      <th className="zahl">Fälle</th></tr>
                </thead>
                <tbody>
                  {(b.liegezeit ?? []).map((z: any) => (
                    <tr key={z.gewerk} style={{ cursor: 'default' }}>
                      <td>{GEWERK_TEXT[z.gewerk] ?? z.gewerk}</td>
                      <td className="zahl">{menge(z.schnitt)}</td>
                      <td className="zahl">{menge(z.faelle)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="klein leise" style={{ marginBottom: 0 }}>
                Vom Termin bis zur Erledigung. Eine hohe Liegezeit bei
                niedriger Durchlaufzeit heißt meistens: es wird gebaut und
                spät abgehakt.
              </p>
            </>
          )}
        </Karte>
      </div>

      <Karte titel="Abgerechnet" rechts={<Sorte art="bestand" />}>
        {/* JEDE ZAHL FÜHRT ZU IHREN AUFTRÄGEN (31.08.2026).
          *
          * ORIGINALTEXT MARVIN
          *   „wo sehe ich denn die aufträge, die wir in der abrechnung
          *    abgeschlossen haben in der jeweiligen woche?"
          *
          * Diese fünf Kacheln waren die einzigen im ganzen Bildschirm ohne
          * Klick - und die Woche fehlte ganz. Beides gehört zusammen: die
          * Zahl beantwortet „wie viele", der Klick „welche". */}
        <div className="kachelreihe">
          <Kachel wert={menge(b.abrechnung?.heute)} was="heute"
                  klick={() => setFrage({
                    titel: 'Heute abgerechnet',
                    w: { was: 'abgerechnet', von: heute, bis: heute } })} />
          <Kachel wert={menge(b.abrechnung?.woche)} was="diese Woche"
                  klick={() => setFrage({
                    titel: 'In dieser Woche abgerechnet',
                    w: { was: 'abgerechnet', von: wochenAnfang, bis: heute } })} />
          <Kachel wert={menge(b.abrechnung?.zeitraum)} was="im Zeitraum"
                  klick={() => setFrage({
                    titel: 'Im Zeitraum abgerechnet',
                    w: { was: 'abgerechnet', von: b.zeitVon, bis: b.zeitBis } })} />
          <Kachel wert={menge(b.abrechnung?.monat)} was="dieser Monat"
                  klick={() => setFrage({
                    titel: 'In diesem Monat abgerechnet',
                    w: { was: 'abgerechnet', von: monatsAnfang, bis: heute } })} />
          <Kachel wert={menge(b.abrechnung?.jahr)} was="dieses Jahr"
                  klick={() => setFrage({
                    titel: 'In diesem Jahr abgerechnet',
                    w: { was: 'abgerechnet', von: jahresAnfang, bis: heute } })} />
          <Kachel wert={menge(b.abrechnung?.gesamt)} was="gesamt" />
        </div>

        {/* DIE LETZTEN ZWÖLF WOCHEN. Der Monatsbalken darunter sagt, ob das
            JAHR läuft; diese Reihe sagt, ob DIESE Woche läuft - und sie ist
            die Zahl, nach der freitags gefragt wird. */}
        {(b.abrWochen ?? []).length > 0 && (
          <Wochenbalken zeilen={b.abrWochen}
                        titel="Abgerechnet je Kalenderwoche"
                        klick={(w: any) => setFrage({
                          titel: `KW ${w.kw}/${w.jahr}: abgerechnet`,
                          w: { was: 'abgerechnet', von: w.montag,
                               bis: tageSpaeter(w.montag, 6) } })} />
        )}
        {(b.abrMonate ?? []).length > 0 && (
          <table className="liste" style={{ marginTop: 12 }}>
            <thead>
              <tr><th>Monat</th><th className="zahl">abgerechnet</th>
                  <th style={{ width: '35%' }} /></tr>
            </thead>
            <tbody>
              {(b.abrMonate ?? []).map((m: any) => {
                const hoechst = Math.max(
                  ...(b.abrMonate ?? []).map((x: any) => Number(x.n)));
                return (
                  <tr key={m.monat} style={{ cursor: 'default' }}>
                    <td className="monatzelle">{monat(m.monat)}</td>
                    <td className="zahl">{menge(m.n)}</td>
                    <td><Balken wert={Number(m.n)} von={hoechst} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="klein leise" style={{ marginBottom: 0 }}>
          „Abgerechnet" heißt: es gibt ein LEB und keine Position steht mehr offen (wie der Chip „abgerechnet" in der Auftragsliste). Ältere Aufträge ohne LEB zählen mit dem Tag, an dem die Dokumentation abgehakt wurde.
          {Number(b.ohne_datum) > 0 && (
            <>
              {' '}<b className="rot">{menge(b.ohne_datum)}</b> Aufträge haben
              den Haken OHNE Datum - sie fallen aus jeder Monatszahl heraus.
              Ohne diese Zeile summierten sich die Monate auf weniger als das
              Jahr, ohne dass jemand sagen könnte warum.
            </>
          )}
        </p>
      </Karte>

      {b.geld && <Geld g={b.geld} von={b.zeitVon} bis={b.zeitBis} />}
    </>
  );
}


/** Euro, deutsch, mit zwei Nachkommastellen. */
function euro(w: unknown): string {
  const n = Number(w ?? 0);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('de-DE',
    { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * Was wirklich geflossen ist.
 *
 * ORIGINALTEXT MARVIN (01.09.2026)
 *   „Die Gutschriften werden ausgewertet die wir als email erhalten und
 *    einlesen, so dass wir sehen, wie viel wir tatsächlich dann in dem Monat
 *    verdient haben ... das ist der Nettozufluss, Gewinn bzw. Umsatz der
 *    Firma für diese Woche bzw. der Monat oder den Zeitraum."
 *
 * DREI ZAHLEN UND EIN SATZ, UND DER SATZ IST DER WICHTIGERE TEIL. Der Rest
 * ist NICHT der Gewinn: darin fehlen Löhne, Fahrzeuge, Material,
 * Versicherungen, Verwaltung - alles, was dieses System nicht kennt. **Eine
 * Aufschrift, die mehr behauptet als die Zahl hergibt, ist schlimmer als eine
 * fehlende Zahl: man rechnet damit.** Dieselbe Lehre wie bei „Verlegt 15
 * Meter" am 01.09.2026.
 *
 * SIE STEHT NUR DA, WENN DAS RECHT DA IST - und ohne das Recht schickt der
 * Server sie gar nicht erst mit. Eine Anzeige, die etwas verbirgt, hat es
 * trotzdem im Speicher.
 */
function Geld({ g, von, bis }: { g: any; von: string; bis: string }) {
  const hoechst = Math.max(1,
    ...(g.monate ?? []).map((m: any) =>
      Math.max(Number(m.zufluss ?? 0), Number(m.abfluss ?? 0))));

  return (
    <Karte titel="Geld" rechts={<Sorte art="zeitraum" />}>
      <div className="kachelreihe">
        <div className="kachel">
          <div className="wert" style={{ color: 'var(--erledigt)' }}>
            {euro(g.zufluss)}
          </div>
          <div className="was">Zufluss ({menge(g.gutschriften)} Gutschriften)</div>
        </div>
        <div className="kachel">
          <div className="wert" style={{ color: 'var(--ueberfaellig)' }}>
            {euro(g.abfluss)}
          </div>
          <div className="was">
            Abfluss ({menge(g.rechnungen)} Rechnungen der Partner)
          </div>
        </div>
        <div className="kachel">
          <div className="wert">{euro(g.rest)}</div>
          <div className="was">Rest nach Nachunternehmern</div>
        </div>
        <div className="kachel">
          <div className="wert">
            {menge(g.leb_bezahlt)} / {menge(g.leb_dateien)}
          </div>
          <div className="was">LEB abgerechnet, davon mit Gutschrift</div>
        </div>
      </div>

      {/* WELCHER ZEITRAUM, UND DANEBEN MONAT UND JAHR (.358). Marvin,
          14.10.2026: „Das Geld im Zufluss, Abfluss. Diese Anzeige, die
          stimmt nicht. Die bezieht nicht alle Daten." Eingestellt war
          „diese Woche" (ab 12.10.): 3 Gutschriften, keine Partnerrechnung -
          richtig gezählt, aber an keiner Stelle gesagt. Jetzt steht die
          Spanne an der Zahl, und Monat und Jahr stehen daneben, unabhängig
          vom Filter. */}
      <p className="klein leise" style={{ margin: '4px 0 8px' }}>
        Die Zahlen oben gelten für {spanneText(von, bis)} (Zeitraum oben
        auf der Seite).
      </p>
      {(() => {
        const jetzt = new Date();
        const m = `${jetzt.getFullYear()}-${String(jetzt.getMonth() + 1).padStart(2, '0')}`;
        const j = String(jetzt.getFullYear());
        const mo = (g.monate ?? []).find((x: any) => x.monat === m) ?? {};
        const ja = (g.monate ?? []).filter((x: any) => String(x.monat).startsWith(j))
          .reduce((a: any, x: any) => ({ zufluss: a.zufluss + Number(x.zufluss ?? 0),
                                         abfluss: a.abfluss + Number(x.abfluss ?? 0) }),
                  { zufluss: 0, abfluss: 0 });
        return (
          <div className="kachelreihe">
            <div className="kachel">
              <div className="wert">{euro(Number(mo.zufluss ?? 0) - Number(mo.abfluss ?? 0))}</div>
              <div className="was">dieser Monat: {euro(mo.zufluss ?? 0)} Zufluss, {euro(mo.abfluss ?? 0)} Abfluss</div>
            </div>
            <div className="kachel">
              <div className="wert">{euro(ja.zufluss - ja.abfluss)}</div>
              <div className="was">dieses Jahr: {euro(ja.zufluss)} Zufluss, {euro(ja.abfluss)} Abfluss</div>
            </div>
          </div>
        );
      })()}

      {/* DER SATZ, DER DIE ZAHL EINORDNET. Er steht ÜBER dem Verlauf und
          nicht als Fussnote: wer die Kacheln liest, liest ihn mit. */}
      <div className="hinweis" style={{ marginTop: 4 }}>
        <b>Der Rest ist nicht der Gewinn.</b> Darin fehlt alles, was dieses
        System nicht kennt: Löhne, Fahrzeuge, Material, Versicherungen,
        Verwaltung. Es ist der Betrag, der von einem Auftrag übrig bleibt,
        nachdem der Nachunternehmer bezahlt ist.
      </div>

      {(g.monate ?? []).length > 0 && (
        <table className="liste" style={{ marginTop: 12 }}>
          <thead>
            <tr>
              <th>Monat</th>
              <th className="zahl">Zufluss</th>
              <th className="zahl">Abfluss</th>
              <th className="zahl">Rest</th>
              <th style={{ width: '30%' }} />
            </tr>
          </thead>
          <tbody>
            {(g.monate ?? []).map((m: any) => (
              <tr key={m.monat} style={{ cursor: 'default' }}>
                <td className="monatzelle">{monat(m.monat)}</td>
                <td className="zahl">{euro(m.zufluss)}</td>
                <td className="zahl">{euro(m.abfluss)}</td>
                <td className="zahl">
                  {euro(Number(m.zufluss ?? 0) - Number(m.abfluss ?? 0))}
                </td>
                <td><Balken wert={Number(m.zufluss ?? 0)} von={hoechst} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* WAS DIE SUMME UNVOLLSTÄNDIG MACHT, STEHT DANEBEN UND NICHT NUR IM
          PROTOKOLL. Zwei Fälle, zwei Aufgaben - und beide machen die Zahl
          darüber zu klein. **Eine Zahl, die zu klein ist, sieht aus wie eine
          Zahl.** */}
      {(g.offen ?? []).length > 0 && (
        <>
          <div className="hinweis warnung" style={{ marginTop: 12 }}>
            <b>{menge(g.offen.length)}{g.offen.length >= 50 ? '+' : ''} Gutschrift(en)
            ohne lesbaren Betrag oder ohne Auftrag.</b>
            {' '}Ein fehlender Betrag heisst: das PDF war nicht lesbar - er
            lässt sich nachtragen. Ohne Auftrag zählt die Gutschrift im
            Zufluss mit, fehlt aber beim Auftrag. Zugeordnet wird stündlich
            von selbst über Bestellnummer, SM-Nummer, KLS-ID und Anschrift aus
            dem Beleg; was dann noch hier steht, passt auf keinen oder auf
            mehrere Aufträge (Grund in der Spalte rechts).
          </div>
          <table className="liste">
            <thead>
              <tr>
                <th style={{ width: 150 }}>Gutschrift</th>
                <th style={{ width: 130 }}>Beleg</th>
                <th style={{ width: 110 }}>Datum</th>
                <th className="zahl" style={{ width: 120 }}>Betrag</th>
                <th>Was fehlt</th>
              </tr>
            </thead>
            <tbody>
              {g.offen.map((o: any) => (
                <tr key={o.id} style={{ cursor: 'default' }}>
                  <td className="klein"><code>{o.nummer}</code></td>
                  <td className="klein">{o.belegnummer ?? '-'}</td>
                  <td className="klein">{o.datum ? datum(o.datum) : '-'}</td>
                  <td className="zahl klein">
                    {o.betrag != null ? euro(o.betrag)
                      : <span className="rot">nicht gelesen</span>}
                  </td>
                  <td className="klein leise">
                    {o.ohne_auftrag && <b>Kein Auftrag. </b>}
                    {o.gelesen_befund}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {/* UND DIE ZAHL JE BAUSTELLE.
          ORIGINALTEXT MARVIN (06.09.2026)
            „was dann faktisch pro auftrag bei mir bleibt und das kann ich
             dann alles auswerten."
          Die Kacheln oben sind die Zahl für den MONAT; darunter steht dieselbe
          Frage je AUFTRAG. Sie kommen aus denselben zwei Sichten, und
          Migration 160 misst, dass sie sich zur selben Summe addieren. */}
      <GeldJeAuftrag von={von} bis={bis} />
    </Karte>
  );
}

/**
 * Die Aufträge hinter einer Zahl.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „und generell will ich, dass du nun alles einmal fertig programmierst,
 *    schaue dir dazu die vorlagen an und baue alle punkte 1:1 NEU in dem
 *    neuen system."
 *
 * Früher musste man dafür von Hand filtern und hat sich dabei regelmässig
 * verzählt. Eine Zahl ohne Weg dahinter ist eine Behauptung.
 */
function AuftragsListe({
  frage, zu, oeffne,
}: { frage: Frage; zu: () => void; oeffne?: (id: string) => void }) {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  useEffect(() => {
    api.auswertungAuftraege(frage.w)
      .then(setD).catch((f) => setFehler(ausFehler(f)));
  }, [JSON.stringify(frage.w)]);

  return (
    <Dialog titel={frage.titel} breite={860} zu={zu}
            untertitel={d ? `${d.zeilen.length} ${d.zeilen.length === 1
              ? 'Auftrag' : 'Aufträge'}` : undefined}>
      <Fehlerkasten lage={fehler} />
      {!d ? (!fehler && <Leer text="Wird gezählt." />) : (
        <>
          {/* NICHT NUR DIE ZEILEN. Wer 500 sieht und nicht weiss, dass es 700
              sind, zählt falsch. */}
          {d.gekappt && (
            <div className="hinweis warnung">
              <b>Es sind mehr als 500.</b>
              <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
                Angezeigt sind die ersten 500. Für die vollständige Liste den
                Zeitraum enger stellen oder in der Arbeitsliste filtern.
              </div>
            </div>
          )}
          {d.zeilen.length === 0 ? (
            <Leer text="Dahinter steht kein Auftrag." />
          ) : (
            <table className="liste">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>KLS-ID</th>
                  <th>Adresse</th>
                  <th style={{ width: 150 }}>Gemeinde</th>
                  <th style={{ width: 130 }}>Zustand</th>
                </tr>
              </thead>
              <tbody>
                {d.zeilen.map((z: any) => (
                  <tr key={z.id}
                      style={{ cursor: oeffne ? 'pointer' : 'default' }}
                      onClick={oeffne ? () => oeffne(z.id) : undefined}>
                    <td className="klein">
                      {z.kls_id}
                      {z.sm_nummer && (
                        <div className="klein leise">{z.sm_nummer}</div>
                      )}
                    </td>
                    <td>
                      {z.strasse} {z.hausnummer}{z.hausnummer_zusatz ?? ''}
                      <div className="klein leise">
                        {z.plz} {z.ort_text}
                        {z.auftragstyp_mds && ', ' + z.auftragstyp_mds}
                      </div>
                    </td>
                    <td className="klein">{z.gemeinde ?? '-'}</td>
                    <td>
                      {z.zustand && (
                        <span className={'marke-zustand z-' + z.zustand}>
                          {ZUSTAND_TEXT[z.zustand] ?? z.zustand}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </Dialog>
  );
}

/**
 * Die Auswertung.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „und generell will ich, dass du nun alles einmal fertig programmierst,
 *    schaue dir dazu die vorlagen an und baue alle punkte 1:1 NEU in dem
 *    neuen system."
 *
 * Sie hatte bis zum 24.08.2026 keinen Zeitraum: sie zeigte immer die letzten
 * zwölf Monate und sagte es nirgends. Eine Zahl, deren Zeitraum man nicht
 * kennt, lässt sich mit nichts vergleichen.
 */
export function Auswertung({
  oeffne,
}: { oeffne?: (id: string) => void }) {
  const [d, setD] = useState<any>(null);
  const [von, setVon] = useState<string>('');
  const [bis, setBis] = useState<string>('');
  const [frage, setFrage] = useState<Frage | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  useEffect(() => {
    api.auswertung(von || undefined, bis || undefined)
      .then((a) => {
        setD(a);
        // DIE ENDEN KOMMEN VOM SERVER ZURÜCK. Beim ersten Laden steht hier
        // nichts, und der Server hat die letzten zwölf Monate genommen; ohne
        // das Zurückschreiben stünden die Felder leer über echten Zahlen.
        setVon((v) => v || a.von);
        setBis((b) => b || a.bis);
      })
      .catch((f) => setFehler(ausFehler(f)));
  }, [von, bis]);

  if (!d) {
    return (
      <>
        <Kopf titel="Auswertung" weg="Zahlen aus den echten Daten" />
        <div className="inhalt"><Fehlerkasten lage={fehler} /></div>
      </>
    );
  }

  function spanne(monate: number) {
    const heute = d.bis;
    setBis(heute);
    setVon(vorMonaten(heute, monate));
  }

  /* DIE ZEITRÄUME, NACH DENEN WIRKLICH GEFRAGT WIRD.
   *
   * ORIGINALTEXT MARVIN (31.08.2026)
   *   „bei der filterung fehlt die KW"
   *
   * ER HAT DIE LISTE DES ALTEN BERICHTS IM KOPF, und sie ist die richtige:
   * Heute, KW, Monat, Quartal, Jahr. Hier standen drei Knöpfe, und alle drei
   * hiessen „die letzten N Monate" - eine Spanne, die IRGENDWO anfängt.
   * **„Diese Woche" und „die letzten sieben Tage" sind nicht dasselbe**, und
   * wer freitags fragt, was die Woche gebracht hat, meint die Woche.
   *
   * Gerechnet wird in ORTSZEIT über `Date`-Objekte ohne Uhrzeit; die Wochen
   * beginnen am MONTAG, wie in ISO 8601 und wie in `date_trunc('week')` auf
   * dem Server. Beide müssen dieselbe Woche meinen, sonst zeigt die Kachel
   * „KW" etwas anderes als die Liste dahinter. */
  /* Die zweite Fassung in dieser Datei, ebenfalls gefallen. */
  function setzeSpanne(vonT: Tag, bisT: Tag) {
    setVon(vonT); setBis(bisT);
  }
  /* `heuteD()` GAB EIN `Date` AUF MITTAG DER BETRACHTERUHR, und `setzeSpanne`
     las es in Berlin. Der Mittag hat den Bruch gedaempft und nicht behoben.
     Ein Tag ist jetzt eine Zeichenkette und braucht keine Uhr. */
  const heuteD = () => heuteTag();
  /** Eine ganze Woche, Montag bis Sonntag. `versatz` 0 ist die laufende. */
  function setzeWoche(versatz: number) {
    const mo = plusTage(montagVon(heuteD()), versatz * 7);
    setzeSpanne(mo, plusTage(mo, 6));
  }
  /** Der letzte Tag eines Monats, ohne Uhr: der Tag vor dem Ersten danach. */
  function letzterImMonat(jahr: number, monat0: number): Tag {
    const j = jahr + Math.floor(monat0 / 12);
    const m = ((monat0 % 12) + 12) % 12;
    return plusTage(`${j}-${String(m + 1).padStart(2, '0')}-01`, -1);
  }
  const ZEITRAEUME: [string, () => void, string][] = [
    ['Heute', () => { const h = heuteD(); setzeSpanne(h, h); },
     'Nur der heutige Tag.'],
    ['Diese KW', () => setzeWoche(0),
     'Montag bis Sonntag der laufenden Woche.'],
    /* DIE LETZTE KW GEHÖRT DAZU, und sie ist die häufiger gebrauchte.
       Marvin, 01.09.2026: „hier muss die letzte KW auch auszuwählen sein".
       Die laufende Woche ist noch nicht fertig; wer fragt „was haben wir
       geschafft", meint fast immer die VERGANGENE - dieselbe Regel wie bei
       der Kapazitätsmeldung, wo ausdrücklich nur abgeschlossene Wochen
       verglichen werden. Am Montagmorgen ist „diese KW" sonst leer, und eine
       leere Auswertung liest sich wie ein kaputter Bildschirm. */
    ['Letzte KW', () => setzeWoche(-1),
     'Montag bis Sonntag der vergangenen Woche.'],
    ['Dieser Monat', () => {
      const h = heuteD();
      const j = jahrVon(h); const m = Number(h.slice(5, 7)) - 1;
      setzeSpanne(`${h.slice(0, 7)}-01`, letzterImMonat(j, m + 1));
    }, 'Vom Ersten bis zum Letzten.'],
    ['Quartal', () => {
      const h = heuteD();
      const j = jahrVon(h);
      const q = Math.floor((Number(h.slice(5, 7)) - 1) / 3) * 3;
      setzeSpanne(`${j}-${String(q + 1).padStart(2, '0')}-01`,
                  letzterImMonat(j, q + 3));
    }, 'Das laufende Vierteljahr.'],
    ['Jahr', () => {
      const j = jahrVon(heuteD());
      setzeSpanne(`${j}-01-01`, `${j}-12-31`);
    }, 'Vom 01.01. bis zum 31.12.'],
    ['12 Monate', () => spanne(12), 'Die letzten 12 Monate.'],
  ];

  const maxFirma = Math.max(
    ...d.jeFirma.map((f: any) => f.tiefbau + f.montage), 1);
  const maxGem = Math.max(...d.jeGemeinde.map((g: any) => g.auftraege), 1);
  const maxMonat = Math.max(
    ...d.jeMonat.map((x: any) => x.tiefbau + x.einblasen + x.spleissen), 1);
  const maxTyp = Math.max(...d.typen.map((t: any) => t.n), 1);
  const zeit = { von: d.von, bis: d.bis };

  return (
    <>
      <Kopf titel="Auswertung" weg="Zahlen aus den echten Daten"
            rechts={d.geld ? (
              /* .484: die Wochenauswertung als PDF, dieselbe Seite wie sonntags im Postfach. */
              <a className="knopf" href="/api/auswertung/woche.pdf" target="_blank" rel="noreferrer">Wochenauswertung (PDF)</a>
            ) : undefined} />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />

        <Karte titel="Zeitraum">
          {/* BESCHRIFTUNG UND FELD BLEIBEN ZUSAMMEN.
              Ohne die Klammer bricht die Reihe auf dem Telefon zwischen „bis"
              und seinem Feld um, und dann steht „bis" neben dem VON-Feld.
              Das ist keine Unschönheit, das ist eine falsche Angabe. */}
          <div className="reihe">
            <span className="feldpaar">
              <label className="klein leise" htmlFor="ausw-von">von</label>
              <input id="ausw-von" type="date" value={von}
                     onChange={(e) => setVon(e.target.value)} />
            </span>
            <span className="feldpaar">
              <label className="klein leise" htmlFor="ausw-bis">bis</label>
              <input id="ausw-bis" type="date" value={bis}
                     onChange={(e) => setBis(e.target.value)} />
            </span>
            {ZEITRAEUME.map(([name, setzen, warum]) => (
              <button key={name} className="knopf klein" title={warum}
                      onClick={setzen}>
                {name}
              </button>
            ))}
          </div>
          {/* DIE KALENDERWOCHE STEHT DA, SOBALD DER ZEITRAUM EINE IST.
              Marvin, 01.09.2026: „die KW muss oben irgendwo mal stehen." Auf
              der Baustelle wird in Wochen geredet („die KW 36 ist voll"), im
              Filter standen zwei Datumsfelder. Sie steht NUR bei einer ganzen
              Woche von Montag bis Sonntag: eine behauptete Woche über neun
              Tagen wäre schlimmer als gar keine Angabe. */}
          {kwSpanne(von, bis) && (
            <div className="kwband">{kwSpanne(von, bis)}</div>
          )}
          <p className="klein leise" style={{ marginBottom: 0 }}>
            Der Zeitraum gilt NICHT für alles, und das ist Absicht. Was in ihm
            passiert ist, hängt daran: erledigte Gewerke, neue Aufträge,
            erzeugte Papiere. Was heute so IST, hängt nicht daran: offen,
            überfällig, Zustand, Auftragstyp. Jede Karte sagt, welche der
            beiden Sorten sie zeigt. Und jede Zahl lässt sich anklicken: dann
            stehen die Aufträge dahinter.
          </p>
        </Karte>

        <Karte titel="Was in diesem Zeitraum passiert ist"
               rechts={<Sorte art="zeitraum" />}>
          <div className="kachelreihe">
            <Kachel wert={menge(d.imZeitraum.neu)} was="neue Aufträge"
                    klick={() => setFrage({
                      titel: 'Neu hereingekommen',
                      w: { was: 'neu', ...zeit } })} />
            <Kachel wert={menge(d.imZeitraum.erledigt)} was="erledigte Gewerke"
                    farbe="var(--erledigt)"
                    klick={() => setFrage({
                      titel: 'Aufträge mit einem erledigten Gewerk',
                      w: { was: 'erledigt', ...zeit } })} />
            {/* SEIT .358 MIT WEG DAHINTER. Marvin, 14.10.2026: „sollen auch
                anklickbar sein und die Details entsprechend in einem Pop-up
                anzeigen wie bei den anderen auch". Die Zahl zählt Dateien,
                die Liste zeigt die Aufträge, zu denen sie gehören - so steht
                es im Titel. */}
            <Kachel wert={menge(d.imZeitraum.papiere)} was="erzeugte Papiere"
                    klick={() => setFrage({
                      titel: 'Aufträge mit einem erzeugten Papier',
                      w: { was: 'papiere', ...zeit } })} />
            <Kachel wert={menge(d.imZeitraum.leb)} was="LEB-Dateien"
                    klick={() => setFrage({
                      titel: 'Aufträge mit einer LEB-Datei',
                      w: { was: 'leb', ...zeit } })} />
            <Kachel wert={menge(d.imZeitraum.rb)} was="Rotberichtigungen"
                    klick={() => setFrage({
                      titel: 'Aufträge mit einer Rotberichtigung',
                      w: { was: 'rb', ...zeit } })} />
          </div>
        </Karte>

        {/* ===================================================================
            DER WOCHENBERICHT

            ORIGINALTEXT MARVIN (31.08.2026)
              „zur auswertung habe ich mal hier die email, die an mich
               versendet wird, wo die relevanten daten drin sind. das gehört
               in die auswerung rein. später verfeinern wir sie."

            Er hat die „MDS Auswertung KW 35/2026" geschickt. Was darin steht,
            stand auf diesem Bildschirm nicht - und es sind genau die Fragen,
            die man montags stellt: wie viel ist baubar, woran hängt der Rest,
            wann sind wir durch, wie lange dauert ein Auftrag, wie viel ist
            abgerechnet.

            GERECHNET WIRD NICHT HIER. Die Stände kommen aus
            `v_auftrag_baustand`, die Prognose aus `regeln/prognose.ts`. Was
            hier steht, ist die Anzeige - und die Erklärung daneben.
            =================================================================== */}
        {/* DER EINGESTELLTE ZEITRAUM GEHT MIT. Die Kachel „im Zeitraum" in
            der Abrechnung braucht ihn für den Weg dahinter, und er soll aus
            DERSELBEN Quelle kommen wie die Zahl darüber: den Enden, die der
            Server zurückgegeben hat. */}
        {d.bericht && (
          <Bericht b={{ ...d.bericht, zeitVon: d.von, zeitBis: d.bis,
                        geld: d.geld }}
                   setFrage={setFrage} />
        )}

        <Karte titel="Wie es heute steht" rechts={<Sorte art="bestand" />}>
          <div className="kachelreihe">
            {d.zustand.map((z: any) => (
              <Kachel key={z.zustand} wert={menge(z.n)}
                was={ZUSTAND_TEXT[z.zustand] ?? z.zustand}
                farbe={z.zustand === 'ueberfaellig'
                  ? 'var(--ueberfaellig)' : undefined}
                klick={() => setFrage({
                  titel: (ZUSTAND_TEXT[z.zustand] ?? z.zustand) + ' heute',
                  w: { was: 'zustand', wert: z.zustand } })} />
            ))}
          </div>
        </Karte>

        <div className="nebeneinander">
          <Karte titel="Gewerke je Firma">
            {/* EINE LEERE TABELLE IST KEINE ANTWORT. Eine Kopfzeile über
                nichts sieht aus wie ein Bildschirm, der noch lädt. */}
            {d.jeFirma.length === 0 && (
              <Leer text="Noch ist kein Gewerk an eine Firma vergeben." />
            )}
            {d.jeFirma.length > 0 && (
            <table className="liste">
              <thead>
                <tr>
                  <th>Firma</th>
                  <th className="zahl" style={{ width: 90 }}>
                    erledigt<Sortewort art="zeitraum" />
                  </th>
                  <th className="zahl" style={{ width: 70 }}>
                    offen<Sortewort art="bestand" />
                  </th>
                  <th className="zahl" style={{ width: 90 }}>
                    überfällig<Sortewort art="bestand" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {d.jeFirma.map((f: any) => (
                  <tr key={f.id} style={{ cursor: 'default' }}>
                    <td>
                      {f.firma}
                      {f.eigenpersonal && (
                        <span className="gewerk" style={{ marginLeft: 6 }}>
                          eigen
                        </span>
                      )}
                      {/* EINE ABGESCHALTETE FIRMA MIT OFFENEN GEWERKEN.
                        *
                        * ORIGINALTEXT MARVIN (31.08.2026)
                        *   „Steht auch wieder Wagner drin. Wagner ist inaktiv.
                        *    Warum ist das so der Fall?"
                        *
                        * Weil an ihr noch Gewerke hängen. Herausfiltern wäre
                        * das Falsche: dann verschwände genau die Zeile, die
                        * etwas bedeutet - Arbeit, die niemandem mehr gehört -,
                        * und die Summen stimmten nicht mehr mit der
                        * Auftragsliste überein. Die Zeile sagt es jetzt, und
                        * die Zahlen daneben sagen, wie viel umzuhängen ist. */}
                      {f.aktiv === false && (
                        <span className="gewerk" style={{ marginLeft: 6,
                                color: 'var(--ueberfaellig)',
                                borderColor: 'var(--ueberfaellig)' }}
                              title="Diese Firma ist abgeschaltet. Die Gewerke hängen noch an ihr.">
                          inaktiv
                        </span>
                      )}
                      <Balken wert={f.tiefbau + f.montage} von={maxFirma} />
                      <div className="klein leise">
                        {f.tiefbau} Tiefbau, {f.montage} Montage
                      </div>
                    </td>
                    <td className="zahl">
                      <button className="alslink" onClick={() => setFrage({
                        titel: f.firma + ': erledigt im Zeitraum',
                        w: { was: 'erledigt', firma: f.id, ...zeit } })}>
                        {f.erledigt_im_zeitraum}
                      </button>
                    </td>
                    <td className="zahl">
                      <button className="alslink" onClick={() => setFrage({
                        titel: f.firma + ': heute offen',
                        w: { was: 'offen', firma: f.id } })}>
                        {f.offen}
                      </button>
                    </td>
                    <td className="zahl">
                      {f.ueberfaellig ? (
                        <button className="alslink"
                                style={{ color: 'var(--ueberfaellig)' }}
                                onClick={() => setFrage({
                                  titel: f.firma + ': heute überfällig',
                                  w: { was: 'ueberfaellig', firma: f.id } })}>
                          {f.ueberfaellig}
                        </button>
                      ) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </Karte>

          <Karte titel="Aufträge je Gemeinde">
            {d.jeGemeinde.length === 0 && (
              <Leer text="Keinem Auftrag ist bisher eine Gemeinde zugeordnet." />
            )}
            {d.jeGemeinde.length > 0 && (
            <table className="liste">
              <thead>
                <tr>
                  <th>Gemeinde</th>
                  <th className="zahl" style={{ width: 80 }}>
                    gesamt<Sortewort art="bestand" />
                  </th>
                  <th className="zahl" style={{ width: 70 }}>
                    neu<Sortewort art="zeitraum" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {d.jeGemeinde.map((g: any) => (
                  <tr key={g.id} style={{ cursor: 'default' }}>
                    <td>
                      {g.gemeinde}
                      {!g.gepflegt && (
                        <span className="klein"
                              style={{ color: 'var(--ueberfaellig)' }}>
                          {' '}nicht gepflegt
                        </span>
                      )}
                      <Balken wert={g.auftraege} von={maxGem} />
                    </td>
                    <td className="zahl">
                      <button className="alslink" onClick={() => setFrage({
                        titel: g.gemeinde + ': heute offen',
                        w: { was: 'offen', gemeinde: g.id } })}>
                        {g.auftraege}
                      </button>
                    </td>
                    <td className="zahl">
                      {g.neu_im_zeitraum ? (
                        <button className="alslink" onClick={() => setFrage({
                          titel: g.gemeinde + ': neu im Zeitraum',
                          w: { was: 'neu', gemeinde: g.id, ...zeit } })}>
                          {g.neu_im_zeitraum}
                        </button>
                      ) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </Karte>
        </div>

        <Karte titel="Je Gewerk">
          <table className="liste">
            <thead>
              <tr>
                <th style={{ width: 200 }}>Gewerk</th>
                <th className="zahl" style={{ width: 120 }}>
                  erledigt<Sortewort art="zeitraum" />
                </th>
                <th className="zahl" style={{ width: 100 }}>
                  offen<Sortewort art="bestand" />
                </th>
                <th className="zahl" style={{ width: 110 }}>
                  überfällig<Sortewort art="bestand" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {d.jeGewerk.map((g: any) => (
                <tr key={g.gewerk} style={{ cursor: 'default' }}>
                  <td>
                    <b>{GEWERK_TEXT[g.gewerk] ?? g.gewerk}</b>
                    <span className="gewerk" style={{ marginLeft: 8 }}>
                      {g.gewerk}
                    </span>
                  </td>
                  <td className="zahl">{g.erledigt}</td>
                  <td className="zahl">{g.offen}</td>
                  <td className="zahl"
                      style={{ color: g.ueberfaellig
                        ? 'var(--ueberfaellig)' : undefined }}>
                    {g.ueberfaellig || ''}
                  </td>
                  <td>
                    <Balken wert={g.erledigt}
                            von={Math.max(...d.jeGewerk.map(
                              (x: any) => x.erledigt), 1)}
                            farbe="var(--erledigt)" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="klein leise" style={{ marginBottom: 0 }}>
            Hier steht KEIN Klickweg auf ein einzelnes Gewerk: die Liste
            dahinter wäre eine Liste von Gewerken, und der Bildschirm daneben
            kennt nur Aufträge. Wer die Gewerke selbst sucht, findet sie im
            Terminplaner.
          </p>
        </Karte>

        <Karte titel="Erledigte Gewerke je Monat"
               rechts={<Sorte art="zeitraum" />}>
          {d.jeMonat.length === 0 ? (
            <Leer text="In diesem Zeitraum wurde nichts erledigt." />
          ) : (
            <table className="liste">
              <thead>
                <tr>
                  <th style={{ width: 110 }}>Monat</th>
                  <th className="zahl" style={{ width: 100 }}>Tiefbau</th>
                  <th className="zahl" style={{ width: 100 }}>Einblasen</th>
                  <th className="zahl" style={{ width: 100 }}>Spleißen</th>
                  <th>Verlauf</th>
                </tr>
              </thead>
              <tbody>
                {d.jeMonat.map((m: any) => (
                  <tr key={m.monat} style={{ cursor: 'default' }}>
                    {/* „2025-10" ist die Sortierform der Datenbank, nicht
                        die Leseform. Marvin, 25.08.2026: „generell soll das
                        datumsformat und uhrzeit europäisch sein." */}
                    <td className="klein">{monat(m.monat)}</td>
                    <td className="zahl">{m.tiefbau}</td>
                    <td className="zahl">{m.einblasen}</td>
                    <td className="zahl">{m.spleissen}</td>
                    <td>
                      <Balken wert={m.tiefbau + m.einblasen + m.spleissen}
                              von={maxMonat} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Karte>

        {/* LEISTUNGSPOSITIONEN, AUS „AUFMASS UND ABRECHNUNG" HIERHER (.230). */}
        <Leistungen von={von} bis={bis} />

        <Karte titel="Auftragstypen" rechts={<Sorte art="bestand" />}>
          <table className="liste">
            <thead>
              <tr>
                <th>Auftragstyp</th>
                <th className="zahl" style={{ width: 100 }}>Aufträge</th>
              </tr>
            </thead>
            <tbody>
              {d.typen.map((t: any) => (
                <tr key={t.typ} style={{ cursor: 'default' }}>
                  <td>
                    {t.typ}
                    <Balken wert={t.n} von={maxTyp} />
                  </td>
                  <td className="zahl">
                    <button className="alslink" onClick={() => setFrage({
                      titel: t.typ, w: { was: 'typ', wert: t.typ } })}>
                      {t.n}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="klein leise" style={{ marginBottom: 0 }}>
            „ohne Angabe" ist kein Fehler des Bildschirms: Aufträge kommen
            automatisiert herein, dabei steht kein Mensch, der ein Auswahlfeld
            ausfüllen könnte. Solange nichts dasteht, gelten alle 3 Gewerke
            als unsere.
          </p>
        </Karte>

        {/* DIE POSITIONEN, UND SIE STEHEN AUF DEMSELBEN BILDSCHIRM.
            Marvin, 03.09.2026: „ich will in der Auswertung wissen ... wie
            viel Volumen wurde abgerufen an Menge und an Preis über welche
            Position."

            EIN EIGENER MENÜPUNKT WÄRE DIE NAHELIEGENDE ANTWORT UND DIE
            FALSCHE: der Zeitraum steht oben, und wer ihn dort einstellt,
            meint ihn für alles darunter. Zwei Bildschirme hiessen zwei
            Zeiträume, und dann vergleicht jemand eine Kachel von hier mit
            einer Zeile von dort. */}
        <Positionsauswertung von={d.von} bis={d.bis} />
      </div>

      {frage && (
        <AuftragsListe frage={frage} oeffne={oeffne}
                       zu={() => setFrage(null)} />
      )}
    </>
  );
}


/**
 * Die Kapazität: erfassen, vorausschauen, melden, vergleichen.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „1. die auswertung, kapazitätsplanung mit (ist/soll-vergleich) soll nur
 *    für mich zugänglich sein, freigeben über rechte, aber getrennt, falls
 *    ich jemandem davon nochmal auch rechte geben will.
 *    2. die komplette kapazitätserfassung die wir gebau haben ist nicht
 *    vorhaben, der memüpunkt fehlt, also bitte bauen und verlinken.
 *    3. der dazugehöhrige ist/soll verlgeich fehlt auch"
 *
 * **Der Menüpunkt war da, die halbe Erfassung nicht.** Was fehlte, ist der
 * Teil, mit dem man die Meldung überhaupt erst BEGRÜNDET: die fünf Positionen
 * mit REAL, THEORETISCH und RESERVE nebeneinander, die Herleitung darunter,
 * die gemessene Leistung je Firma und Gewerk, und die Zwölf-Wochen-
 * Vorausschau, aus der die Zahl kommt, die man einträgt.
 *
 * SECHS REITER, UND JEDER BEANTWORTET EINE ANDERE FRAGE:
 *
 *   Erfassung    Was können wir? (real gemessen gegen theoretisch möglich)
 *   Vorausschau  Was ist in Woche N noch frei?
 *   Melden       Was sagen wir der Telekom zu?
 *   Soll/Ist     Haben wir gehalten, was wir gesagt haben?
 *   Zuschlag     Wie viel legen wir je Woche und Position drauf?
 *   Gebiete      Welche Ortsnetzkennzahl gehört zu welchem Bereich?
 *
 * DER SOLL/IST-VERGLEICH HÄNGT AN SEINEM EIGENEN RECHT (`kapazitaet.sollist`,
 * Migration 076) und wird vom Server gar nicht erst mitgeschickt, wenn es
 * fehlt. Das ist Marvins Punkt 1, und „getrennt" heisst wörtlich getrennt:
 * wer melden darf, sieht damit noch lange nicht, wie gut wir getroffen haben.
 *
 * DREI ZAHLEN, DIE NICHT DASSELBE SAGEN. Soll ist die Zusage, Ist ist das
 * Gebaute, Eingang ist das neu Zugeteilte. Nebeneinander beantworten sie die
 * Frage, ob die letzte Meldung zu vorsichtig war; einzeln beantwortet keine
 * davon irgendetwas.
 */
export function Kapazitaet({ ich }: { ich: Anmelder }) {
  type Reiter = 'erfassung' | 'vorausschau' | 'melden' | 'sollist'
              | 'zuschlag' | 'gebiete';
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [reiter, setReiter] = useState<Reiter>('erfassung');
  const [bereich, setBereich] = useState('');
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [bedarf, setBedarf] = useState<Record<string, string>>({});
  const [kopieren, setKopieren] = useState(false);
  const [kopieWochen, setKopieWochen] = useState('4');
  const [neuOnkz, setNeuOnkz] = useState('');
  const [neuBereich, setNeuBereich] = useState('');
  const [neuOrt, setNeuOrt] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const darfMelden = darf(ich, 'kapazitaet.melden');

  async function laden() { setD(await api.kapazitaet()); }
  useEffect(() => { laden().catch((f) => setFehler(ausFehler(f))); }, []);
  useFrisch(laden);

  useEffect(() => {
    if (!d) return;
    if (!bereich && d.bereiche.length) setBereich(d.bereiche[0]);
  }, [d]);

  // Was für die kommende Woche schon erfasst ist, in die Felder. WAS EIN
  // MENSCH EINGETRAGEN HAT, WIRD VOM VORSCHLAG NIE ÜBERSCHRIEBEN.
  useEffect(() => {
    if (!d || !bereich) return;
    const w: Record<string, string> = {};
    const b: Record<string, string> = {};
    for (const m of d.gemeldet_kommend) {
      if (m.bereich !== bereich) continue;
      w[m.kategorie] = m.menge != null ? String(Number(m.menge)) : '';
      b[m.kategorie] = m.telekom_bedarf != null
        ? String(Number(m.telekom_bedarf)) : '';
    }
    setWerte(w); setBedarf(b);
  }, [d, bereich]);

  if (fehler && !d) {
    return <div className="inhalt"><Fehlerkasten lage={fehler} /></div>;
  }
  if (!d) return null;

  /** Ein Schreibvorgang, immer gleich: laufen lassen, neu laden, melden. */
  async function tue(was: () => Promise<any>, satz?: (a: any) => string) {
    setLaeuft(true); setFehler(null); setMeldung(null);
    try {
      const a = await was();
      await laden();
      if (satz) setMeldung(satz(a));
    } catch (f) { setFehler(ausFehler(f)); } finally { setLaeuft(false); }
  }

  async function speichern() {
    await tue(() => api.kapazitaetMelden({
      jahr: d.kommende_woche.jahr,
      kalenderwoche: d.kommende_woche.kw,
      bereich,
      zeilen: d.kategorien
        .filter((k: string) => String(werte[k] ?? '').trim() !== '')
        .map((k: string) => ({
          kategorie: k,
          menge: Number(String(werte[k]).replace(',', '.')),
          telekom_bedarf: String(bedarf[k] ?? '').trim()
            ? Number(String(bedarf[k]).replace(',', '.')) : null,
        })),
    }), (a) => `${a.gesetzt} Positionen für ${bereich} gespeichert.`);
  }

  async function insPortal() {
    await tue(() => api.kapazitaetPortal(d.kommende_woche.jahr,
                                         d.kommende_woche.kw, bereich));
  }

  /* AUF WEITERE WOCHEN KOPIEREN. Was schon im Portal steht, wird NICHT
     angefasst - und die Antwort sagt, welche Wochen das waren. Ein Kopieren,
     das still etwas auslässt, sieht aus wie eines, das etwas vergessen hat. */
  async function kopiere() {
    const n = Number(kopieWochen);
    await tue(() => api.kapazitaetKopieren({
      jahr: d.kommende_woche.jahr,
      kalenderwoche: d.kommende_woche.kw,
      bereich, wochen: n,
    }), (a) => `${a.positionen} Positionen auf ${a.geschrieben.length} Woche(n) `
             + `kopiert.${a.hinweis ? ' ' + a.hinweis : ''}`);
    setKopieren(false);
  }

  async function zuschlag(jahr: number, kw: number, kat: string, wert: string) {
    await tue(() => api.kapazitaetZuschlag({
      jahr, kalenderwoche: kw, kategorie: kat,
      zuschlag: String(wert).trim() === ''
        ? null : Number(String(wert).replace(',', '.')) }));
  }

  async function gebietSetzen(onkz: string, name: string, ort?: string) {
    await tue(() => api.kapazitaetBereich({ onkz, bereich: name, ort }),
              (a) => `${a.onkz} gehört jetzt zu „${a.bereich}" `
                   + `(${a.auftraege} Aufträge).`);
    setNeuOnkz(''); setNeuBereich(''); setNeuOrt('');
  }

  async function gebietWeg(onkz: string) {
    await tue(() => api.kapazitaetBereichWeg(onkz),
              (a) => a.hinweis ?? `Die Zuordnung für ${a.onkz} ist aufgehoben.`);
  }

  const wochenImVergleich = [...new Map(d.vergleich.map((v: any) =>
    [`${v.jahr}|${v.kalenderwoche}`, v])).values()] as any[];

  const REITER: [Reiter, string][] = [
    ['erfassung', 'Erfassung'],
    ['vorausschau', `Vorausschau (${d.kopf.vorlauf_wochen} Wochen)`],
    ['melden', 'Melden'],
    ...(d.darfSollist
      ? [['sollist', `Soll gegen Ist (${d.wochen} Wochen)`] as [Reiter, string]]
      : []),
    ['zuschlag', 'Zuschlag'],
    ['gebiete', `Gebiete (${d.gebiete.length})`],
  ];

  return (
    <>
      <Kopf titel="Kapazität"
            weg={`Erfassung, Vorausschau und Meldung an das Partnerportal, `
               + `${d.kommende_woche.text}`} />
      <div className="inhalt">
        <Fehlerkasten lage={fehler} />
        {meldung && <div className="hinweis">{meldung}</div>}

        <div className="reihe reiterreihe">
          {REITER.map(([r, t]) => (
            <button key={r} className="knopf" onClick={() => setReiter(r)}
              style={reiter === r
                ? { borderColor: 'var(--akzent)', color: 'var(--akzent)' }
                : undefined}>
              {t}
            </button>
          ))}
        </div>

        {/* ================================================================
            ERFASSUNG
            ================================================================ */}
        {reiter === 'erfassung' && (
          <>
            {/* „0 FREI" UND „AUSGEBUCHT" SEHEN GLEICH AUS, UND DIE
                WAHRSCHEINLICHERE URSACHE IST DIE DRITTE: niemand hat seine
                Tage gemeldet. Eine Null, die aussieht wie eine Messung und
                keine ist, ist die teuerste Zahl auf diesem Bildschirm - sie
                geht als Zusage an die Telekom. */}
            {!d.kopf.verf_tage_vor && (
              <div className="hinweis warnung">
                <b>Für die kommenden {d.kopf.vorlauf_wochen} Wochen ist kein
                einziger Tag als verfügbar gemeldet.</b>
                Die theoretische Kapazität ist deshalb überall null - das
                heißt NICHT „ausgebucht", sondern „niemand hat gemeldet".
                Die Firmen tragen ihre Tage im Verfügbarkeitskalender ein.
              </div>
            )}
            <div className="kachelreihe">
              <Kachel wert={d.kopf.arbeitsvorrat} was="baubare Aufträge" />
              <Kachel wert={menge(d.kopf.komplett_real)}
                      was={`Hausanschlüsse/Woche (real, ${d.kopf.rueckblick_wochen} Wochen)`} />
              <Kachel wert={menge(d.kopf.komplett_frei)}
                      was="frei diese Woche (mit Zuschlag)"
                      farbe="var(--akzent)" />
              <Kachel wert={d.kopf.zuschlag_standard + ' %'}
                      was="Zuschlag (Standard)" />
            </div>

            <Karte titel="Die 5 Leistungspositionen">
              <p className="klein leise" style={{ marginTop: 0 }}>
                <b>REAL</b> ist das Mittel der letzten
                {' '}{d.kopf.rueckblick_wochen} ABGESCHLOSSENEN Wochen,
                auftragsbasiert gezählt. <b>THEORETISCH</b> ist, was der
                Verfügbarkeitskalender der kommenden
                {' '}{d.kopf.vorlauf_wochen} Wochen mal der hinterlegten
                Leistung je Tag hergibt. <b>RESERVE</b> ist der Unterschied.
                {' '}Die laufende Woche geht nie in REAL ein: sie ist noch
                nicht fertig und zöge jede Zahl nach unten.
              </p>
              <p className="klein leise">
                <b>Die Zahlen dürfen sich überschneiden und werden nicht
                addiert.</b> Jede Position sagt, was in dieser Woche
                MÖGLICH wäre, wenn man nur sie bauen würde - wer sie
                aufsummiert, hat dieselbe Firma dreifach gezählt.
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table className="liste" style={{ minWidth: 760 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 34 }}>Nr</th>
                      <th>Leistungsposition</th>
                      <th style={{ width: 200 }}>Gewerke</th>
                      <th className="zahl" style={{ width: 90 }}>Real</th>
                      <th className="zahl" style={{ width: 110 }}>Theoretisch</th>
                      <th className="zahl" style={{ width: 90 }}>Reserve</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.rate.map((r: any) => (
                      <tr key={r.schluessel} style={{ cursor: 'default' }}>
                        <td className="leise">{r.nr}</td>
                        <td>
                          {r.kategorie}
                          <div className="klein leise">{r.herleitung}</div>
                        </td>
                        <td className="klein">{r.gewerke}</td>
                        <td className="zahl">{menge(r.real)}</td>
                        <td className="zahl">{menge(r.theoretisch)}</td>
                        <td className="zahl"
                            style={{ color: r.reserve > 0
                              ? 'var(--erledigt)' : 'var(--text-leise)' }}>
                          {menge(r.reserve)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Karte>

            <Karte titel={`Was die Firmen wirklich geschafft haben `
                        + `(${datumKurz(d.kopf.von)} bis ${datumKurz(d.kopf.bis)})`}>
              {!d.leistung.length ? (
                <Leer text="In diesem Zeitraum wurde kein Gewerk
                            abgeschlossen." />
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="liste" style={{ minWidth: 700 }}>
                    <thead>
                      <tr>
                        <th>Firma</th>
                        <th style={{ width: 90 }}>Gewerk</th>
                        <th className="zahl" style={{ width: 110 }}>Abschlüsse</th>
                        <th className="zahl" style={{ width: 130 }}>gemeldete Tage</th>
                        <th className="zahl" style={{ width: 100 }}>je Tag</th>
                        <th className="zahl" style={{ width: 120 }}>je Woche</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.leistung.map((l: any) => (
                        <tr key={l.id + l.gewerk} style={{ cursor: 'default' }}>
                          <td>
                            {l.firma}
                            {!l.aktiv && <span className="klein leise">
                              {' '}(stillgelegt)</span>}
                          </td>
                          <td className="klein">{GEWERK_TEXT[l.gewerk] ?? l.gewerk}</td>
                          <td className="zahl">{l.abschluesse}</td>
                          <td className="zahl">{l.verf_tage}</td>
                          <td className="zahl">
                            {l.pro_tag == null
                              ? <span className="leise" title="Die Firma hat in
                                  diesem Zeitraum keinen Tag als verfügbar
                                  gemeldet.">-</span>
                              : menge(l.pro_tag)}
                          </td>
                          <td className="zahl">
                            {l.stueck_pro_woche == null
                              ? <span className="leise">-</span>
                              : menge(l.stueck_pro_woche)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {/* „NICHT GEMELDET" IST NICHT „NULL LEISTUNG". Ohne verfügbare
                  Tage lässt sich keine Leistung je Tag rechnen, und ein
                  Strich ist die ehrliche Antwort - eine Null wäre eine
                  Behauptung über eine Firma. */}
              <p className="klein leise" style={{ marginBottom: 0 }}>
                Ein Strich bei „je Tag" heißt: die Firma hat in diesem
                Zeitraum keinen Tag im Verfügbarkeitskalender gemeldet. Das
                ist etwas anderes als „hat nichts geschafft".
              </p>
            </Karte>

            <Karte titel={`Was der Kalender für die nächsten `
                        + `${d.kopf.vorlauf_wochen} Wochen hergibt`}>
              {!d.theoretisch.length ? (
                <Leer text="Keine aktive Firma mit einem Bau-Gewerk." />
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="liste" style={{ minWidth: 720 }}>
                    <thead>
                      <tr>
                        <th>Firma</th>
                        <th style={{ width: 90 }}>Gewerk</th>
                        <th className="zahl" style={{ width: 130 }}>gemeldete Tage</th>
                        <th className="zahl" style={{ width: 110 }}>Standard/Tag</th>
                        <th className="zahl" style={{ width: 110 }}>gemessen/Tag</th>
                        <th className="zahl" style={{ width: 120 }}>je Woche</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.theoretisch.map((t: any) => (
                        <tr key={t.id + t.gewerk} style={{ cursor: 'default' }}>
                          <td>{t.firma}</td>
                          <td className="klein">{GEWERK_TEXT[t.gewerk] ?? t.gewerk}</td>
                          <td className="zahl">{t.verf_tage}</td>
                          <td className="zahl">{menge(t.standard)}</td>
                          {/* WEICHT DER GEMESSENE WERT WEIT AB, STIMMT DER
                              STANDARD NICHT MEHR. Gerechnet wird trotzdem
                              mit dem Standard - wir melden die Zielkapazität
                              -, aber das Auseinanderlaufen muss auffallen. */}
                          <td className="zahl"
                              style={t.weicht_ab
                                ? { color: 'var(--warnung)' } : undefined}
                              title={t.weicht_ab
                                ? 'Der gemessene Wert weicht um mehr als '
                                  + '20 % vom Standard ab. Der Standard '
                                  + 'stimmt vermutlich nicht mehr.'
                                : undefined}>
                            {t.gemessen == null
                              ? <span className="leise">-</span>
                              : menge(t.gemessen)}
                          </td>
                          <td className="zahl">
                            {t.stueck_pro_woche == null
                              ? <span className="leise" title="Keine gemeldeten
                                  Tage in diesem Zeitraum.">-</span>
                              : menge(t.stueck_pro_woche)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="klein leise" style={{ marginBottom: 0 }}>
                Gerechnet wird mit dem <b>Standard</b> und nicht mit dem
                gemessenen Wert: wir melden der Telekom die Zielkapazität, an
                die wir uns herantasten. Der gemessene Wert steht daneben,
                damit auffällt, wenn der Standard nicht mehr stimmt. Die
                Standardwerte stehen in der Konfiguration
                {' '}(TB {menge(d.standardJeGewerk.TB)},
                {' '}EB {menge(d.standardJeGewerk.EB)},
                {' '}SP {menge(d.standardJeGewerk.SP)} je Tag).
              </p>
            </Karte>
          </>
        )}

        {/* ================================================================
            VORAUSSCHAU
            ================================================================ */}
        {reiter === 'vorausschau' && (
          <Karte titel={`Was in welcher Woche noch frei ist`}>
            {!d.kopf.verf_tage_vor && (
              <div className="hinweis warnung">
                <b>Kein Tag ist als verfügbar gemeldet.</b>
                Jede Zahl in dieser Tafel ist deshalb null, und das heißt
                „niemand hat gemeldet" - nicht „ausgebucht".
              </div>
            )}
            <p className="klein leise" style={{ marginTop: 0 }}>
              Die theoretische Kapazität dieser Woche, vermindert um das, was
              darin schon terminiert ist, danach der Zuschlag. <b>T</b> ist
              die Zahl OHNE Zuschlag, die Zahl darunter die mit. Eine
              überbuchte Woche zeigt „0" und nicht „minus 3": das eine ist
              eine Auskunft, das andere eine Verwechslung mit dem Rückstand.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table className="liste" style={{ minWidth: 1010 }}>
                <thead>
                  <tr>
                    <th style={{ width: 190 }}>Woche</th>
                    {/* SIE BRICHT NICHT UM. „TB 0, EB / 1, SP 0" über zwei
                        Zeilen liest sich wie vier Zahlen und sind drei. */}
                    <th className="zahl" style={{ width: 150, whiteSpace: 'nowrap' }}>
                      belegt
                    </th>
                    {d.meta.map((m: any) => (
                      <th key={m.nr} className="zahl" style={{ width: 130 }}
                          title={m.herleitung}>
                        {m.kategorie}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {d.vorausschau.map((w: any) => (
                    <tr key={`${w.jahr}-${w.kalenderwoche}`}
                        style={{ cursor: 'default' }}>
                      <td className="klein">{w.text}</td>
                      <td className="zahl klein leise"
                          style={{ whiteSpace: 'nowrap' }}>
                        TB {w.belegt.TB}, EB {w.belegt.EB}, SP {w.belegt.SP}
                      </td>
                      {w.zeilen.map((z: any) => (
                        <td key={z.schluessel} className="zahl">
                          <span className="leise klein" title="ohne Zuschlag">
                            T {menge(z.frei)}
                          </span>
                          <div style={{
                            color: z.mit_zuschlag > 0
                              ? 'var(--akzent)' : 'var(--text-leise)' }}>
                            {menge(z.mit_zuschlag)}
                            {z.zuschlag != null && (
                              <span className="klein leise"
                                    title="eigener Zuschlag für diese Woche
                                           und diese Position">
                                {' '}+{menge(z.zuschlag)} %
                              </span>
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="klein leise" style={{ marginBottom: 0 }}>
              Die 5 Spalten überschneiden sich und werden nicht addiert.
              Wo kein eigener Zuschlag steht, gilt der Standard
              ({d.zuschlag_standard} %).
            </p>
          </Karte>
        )}

        {/* ================================================================
            MELDEN
            ================================================================ */}
        {reiter === 'melden' && (
          <>
            {!d.bereiche.length ? (
              <div className="hinweis warnung">
                <b>Es ist kein Bereich hinterlegt.</b>
                Ohne die Zuordnung von Ortsnetzkennzahl zu Bereich lässt sich
                nicht sagen, für welches Gebiet gemeldet wird. Sie wird im
                Reiter „Gebiete" gepflegt.
              </div>
            ) : (
              <Karte
                titel={`Meldung für ${d.kommende_woche.text}`}
                rechts={
                  <select value={bereich}
                          onChange={(e) => setBereich(e.target.value)}>
                    {d.bereiche.map((b: string) =>
                      <option key={b} value={b}>{b}</option>)}
                  </select>
                }
              >
                <p className="klein leise" style={{ marginTop: 0 }}>
                  Der Vorschlag ist das Mittel der letzten {d.wochen}
                  {' '}ABGESCHLOSSENEN Wochen, mit Zuschlag
                  {' '}({d.zuschlag_standard} %). Die laufende Woche geht nie
                  ein: sie ist noch nicht fertig und zöge jede Zahl nach unten.
                  {' '}Ein Feld, das leer bleibt, wird nicht gemeldet.
                  {' '}Was für diese Woche FREI ist, steht im Reiter
                  {' '}„Vorausschau".
                </p>
                <table className="liste">
                  <thead>
                    <tr>
                      <th>Leistungsposition</th>
                      <th className="zahl" style={{ width: 100 }}>Vorschlag</th>
                      <th className="zahl" style={{ width: 90 }}>frei</th>
                      <th style={{ width: 130 }}>Wir melden</th>
                      <th style={{ width: 160 }}>Bedarf Telekom</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.kategorien.map((k: string, i: number) => {
                      // Ein Platzhalter „0" ist Rauschen: er sieht aus wie ein
                      // Wert und ist keiner.
                      const v = d.vorschlag[Object.keys(d.vorschlag)[i]!] ?? 0;
                      // WAS DIE VORAUSSCHAU FÜR DIESE WOCHE SAGT, STEHT
                      // DANEBEN. Der Vorschlag kommt aus der Vergangenheit,
                      // die freie Kapazität aus dem Kalender - wer nur eines
                      // von beiden sieht, meldet an der Wirklichkeit vorbei.
                      const frei = d.vorausschau.find((w: any) =>
                        w.jahr === d.kommende_woche.jahr
                        && w.kalenderwoche === d.kommende_woche.kw)
                        ?.zeilen.find((z: any) => z.kategorie === k)
                        ?.mit_zuschlag;
                      return (
                        <tr key={k} style={{ cursor: 'default' }}>
                          <td>{k}</td>
                          <td className="zahl leise">{v}</td>
                          <td className="zahl leise">
                            {frei == null ? '' : menge(frei)}
                          </td>
                          <td>
                            <input type="number" min={0} value={werte[k] ?? ''}
                              disabled={!darfMelden}
                              placeholder={v > 0 ? String(v) : ''}
                              onChange={(e) => setWerte(
                                (w) => ({ ...w, [k]: e.target.value }))} />
                          </td>
                          <td>
                            <input type="number" min={0} value={bedarf[k] ?? ''}
                              disabled={!darfMelden}
                              onChange={(e) => setBedarf(
                                (w) => ({ ...w, [k]: e.target.value }))} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {darfMelden && (
                  <div className="reihe" style={{ marginTop: 12 }}>
                    <button className="knopf haupt" disabled={laeuft}
                            onClick={speichern}>
                      {laeuft ? 'Einen Moment' : 'Meldung speichern'}
                    </button>
                    {/* AUF WEITERE WOCHEN KOPIEREN. Die Kapazität einer Firma
                        ändert sich nicht wöchentlich, die Meldung muss aber
                        jede Woche dastehen. */}
                    <button className="knopf" disabled={laeuft}
                            onClick={() => setKopieren(true)}>
                      Auf weitere Wochen kopieren
                    </button>
                    {/* „ERFASST" UND „IM PORTAL" SIND ZWEI ZEITPUNKTE.
                        Dazwischen sitzt ein Mensch, der sie überträgt, und er
                        überträgt EINEN Bereich. Der Knopf hakte bis zum
                        25.08.2026 die ganze Woche ab, also auch die Bereiche,
                        die nie im Portal standen. Sie sahen danach erledigt
                        aus. Deshalb steht der Bereich jetzt auf dem Knopf. */}
                    <button className="knopf" disabled={laeuft}
                            onClick={insPortal}>
                      {bereich} ist im Portal eingetragen
                    </button>
                    {(() => {
                      const m = d.gemeldet_kommend
                        .find((x: any) => x.bereich === bereich);
                      if (!m) return null;
                      return (
                        <span className="klein leise">
                          {m.ans_portal_am
                            ? `${bereich}: ans Portal am ${datum(m.ans_portal_am)}.`
                            : `${bereich}: erfasst, aber noch nicht im Portal.`}
                        </span>
                      );
                    })()}
                  </div>
                )}
              </Karte>
            )}

            <Karte titel="Welche Orte in welchem Bereich liegen">
              {!d.onkz.length ? (
                <Leer text="Keine Ortsnetzkennzahl einem Bereich zugeordnet." />
              ) : (
                <table className="liste">
                  <thead>
                    <tr><th>Bereich</th>
                      <th className="zahl" style={{ width: 140 }}>ONKz</th>
                      <th className="zahl" style={{ width: 140 }}>Aufträge</th></tr>
                  </thead>
                  <tbody>
                    {d.onkz.map((o: any) => (
                      <tr key={o.bereich} style={{ cursor: 'default' }}>
                        <td>{o.bereich}</td>
                        <td className="zahl">{o.onkz_anzahl}</td>
                        <td className="zahl">{o.auftraege}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Karte>
          </>
        )}

        {/* ================================================================
            SOLL GEGEN IST
            ================================================================ */}
        {reiter === 'sollist' && (
          <>
            {d.sollist && (
              <div className="kachelreihe">
                <Kachel wert={d.sollist.zeilen} was="Zeilen mit Zusage" />
                <Kachel wert={menge(d.sollist.summe_soll)} was="Summe Soll" />
                <Kachel wert={menge(d.sollist.summe_ist)} was="Summe Ist"
                        farbe={d.sollist.summe_ist >= d.sollist.summe_soll
                          ? 'var(--erledigt)' : 'var(--warnung)'} />
                <Kachel
                  wert={d.sollist.treffgenauigkeit == null
                    ? '-' : menge(d.sollist.treffgenauigkeit) + ' %'}
                  was="Zusage gehalten"
                  farbe={(d.sollist.treffgenauigkeit ?? 0) >= 80
                    ? 'var(--erledigt)' : 'var(--warnung)'} />
                <Kachel wert={d.sollist.unter_bedarf}
                        was="Zusagen unter dem Bedarf der Telekom"
                        farbe={d.sollist.unter_bedarf > 0
                          ? 'var(--warnung)' : undefined} />
              </div>
            )}
            <p className="klein leise">
              Positiv heißt: es wurde mehr gebaut als gemeldet, die Meldung war
              zu vorsichtig. Negativ heißt: die Zusage wurde nicht gehalten.
              Ohne Meldung gibt es keine Abweichung, sondern eine fehlende
              Meldung. <b>„Zusage gehalten"</b> zählt ZEILEN und nicht Stücke:
              eine Woche, in der eine Position um 10 danebenliegt und 4
              genau sitzen, ist zu 4 Fünfteln getroffen.
            </p>
            {d.vergleich.map((v: any) => (
              <Karte key={`${v.jahr}-${v.kalenderwoche}-${v.bereich}`}
                     titel={`${v.woche_text}, ${v.bereich}`}
                     rechts={
                       <span className="weg">
                         {v.eingang} neue Aufträge
                         {v.eingang > 0 && ` (${v.eingang_bulk} BULK, ${v.eingang_ibt} IBT)`}
                       </span>
                     }>
                <table className="liste">
                  <thead>
                    <tr>
                      <th>Leistungsposition</th>
                      <th className="zahl" style={{ width: 90 }}>Soll</th>
                      <th className="zahl" style={{ width: 90 }}>Ist</th>
                      <th className="zahl" style={{ width: 110 }}>Bedarf TK</th>
                      <th style={{ width: 260 }}>Abweichung</th>
                    </tr>
                  </thead>
                  <tbody>
                    {v.zeilen.map((z: any) => (
                      <tr key={z.kategorie} style={{ cursor: 'default' }}>
                        <td>{z.kategorie}</td>
                        <td className="zahl">
                          {z.soll ?? <span className="leise">-</span>}
                        </td>
                        <td className="zahl">{z.ist}</td>
                        <td className="zahl leise">{z.telekom_bedarf ?? ''}</td>
                        <td className={'klein'
                          + (z.wert != null && z.wert < 0 ? ' rot' : '')}>
                          {z.text}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Karte>
            ))}
            {!d.vergleich.length && (
              <Karte titel="Nichts zu vergleichen">
                <Leer text="Es gibt weder Meldungen noch abgeschlossene
                            Gewerke in diesem Zeitraum." />
              </Karte>
            )}
          </>
        )}

        {/* ================================================================
            ZUSCHLAG
            ================================================================ */}
        {reiter === 'zuschlag' && (
          <Karte titel="Zuschlag je Woche und Position">
            <p className="klein leise" style={{ marginTop: 0 }}>
              Ein leeres Feld ist KEINE Null: es heisst, dass für diese Woche
              und diese Position der Standard aus der Konfiguration gilt
              ({d.zuschlag_standard} %). Eine Null wäre die ausdrückliche
              Ansage, gar keinen Zuschlag zu geben. Prozent sind Prozent: 15
              heisst +15 %.
            </p>
            {/* Fünf Positionen plus Woche passen bei schmalem Fenster nicht
                nebeneinander. Die Tafel scrollt für sich, die Seite nicht. */}
            <div style={{ overflowX: 'auto' }}>
            <table className="liste" style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th style={{ width: 180 }}>Woche</th>
                  {d.kategorien.map((k: string) => (
                    <th key={k} style={{ width: 130 }}>{k}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* DIE ZWÖLF KOMMENDEN WOCHEN, nicht nur die nächste: der
                    Zuschlag wird VORAUS gesetzt, und wer ihn nur für die
                    nächste Woche setzen kann, setzt ihn nie rechtzeitig. */}
                {[...d.vorausschau, ...wochenImVergleich].map((w: any) => {
                  const jahr = w.jahr;
                  const kw = w.kw ?? w.kalenderwoche;
                  return (
                    <tr key={`${jahr}-${kw}`} style={{ cursor: 'default' }}>
                      <td className="klein">{w.text ?? w.woche_text}</td>
                      {d.kategorien.map((k: string) => {
                        const z = d.zuschlaege.find((x: any) =>
                          x.jahr === jahr && x.kalenderwoche === kw
                          && x.kategorie === k);
                        return (
                          <td key={k}>
                            <input type="number"
                              defaultValue={z ? String(Number(z.zuschlag)) : ''}
                              placeholder={d.zuschlag_standard > 0
                                ? String(d.zuschlag_standard) : ''}
                              disabled={!d.darfZuschlag || laeuft}
                              onBlur={(e) => {
                                const alt = z ? String(Number(z.zuschlag)) : '';
                                if (e.target.value === alt) return;
                                zuschlag(jahr, kw, k, e.target.value);
                              }} />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            {!d.darfZuschlag && (
              <p className="klein leise">
                Zum Ändern fehlt das Recht „Zuschlag setzen".
              </p>
            )}
          </Karte>
        )}

        {/* ================================================================
            GEBIETE
            ================================================================ */}
        {reiter === 'gebiete' && (
          <>
            <Karte titel="Welche Ortsnetzkennzahl zu welchem Bereich gehört">
              <p className="klein leise" style={{ marginTop: 0 }}>
                Die Meldung an das Partnerportal geht <b>je Bereich</b>. Ohne
                diese Zuordnung steht auf dem Kapazitätsbildschirm „(Bereich
                unbekannt)", und die Vorausschau lässt sich nicht aufteilen.
                {' '}Der Name wird abgeschrieben, wie die Telekom ihn führt
                (zum Beispiel „SW_PTI14_Los_6_Koblenz u.U.") - es ist ihre
                Benennung, nicht unsere.
              </p>
              {darfMelden && (
                <div className="reihe" style={{ marginBottom: 12 }}>
                  <div className="feldpaar">
                    <label htmlFor="neu_onkz">ONKz</label>
                    <input id="neu_onkz" value={neuOnkz} style={{ width: 110 }}
                           placeholder="0261"
                           onChange={(e) => setNeuOnkz(e.target.value)} />
                  </div>
                  <div className="feldpaar wachsen">
                    <label htmlFor="neu_bereich">Bereich</label>
                    <input id="neu_bereich" value={neuBereich}
                           list="bereichsliste"
                           onChange={(e) => setNeuBereich(e.target.value)} />
                    <datalist id="bereichsliste">
                      {d.bereiche.map((b: string) =>
                        <option key={b} value={b} />)}
                    </datalist>
                  </div>
                  <div className="feldpaar">
                    <label htmlFor="neu_ort">Ort (freiwillig)</label>
                    <input id="neu_ort" value={neuOrt}
                           onChange={(e) => setNeuOrt(e.target.value)} />
                  </div>
                  <button className="knopf haupt" disabled={laeuft}
                          onClick={() => gebietSetzen(neuOnkz.trim(),
                                                      neuBereich.trim(),
                                                      neuOrt.trim() || undefined)}>
                    Zuordnen
                  </button>
                </div>
              )}
              {!d.gebiete.length ? (
                <Leer text="Keine Zuordnung hinterlegt." />
              ) : (
                <table className="liste">
                  <thead>
                    <tr>
                      <th style={{ width: 110 }}>ONKz</th>
                      <th>Bereich</th>
                      <th style={{ width: 180 }}>Ort</th>
                      <th className="zahl" style={{ width: 110 }}>Aufträge</th>
                      {darfMelden && <th style={{ width: 90 }}></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {d.gebiete.map((g: any) => (
                      <tr key={g.onkz} style={{ cursor: 'default' }}>
                        <td className="einfarbig">{g.onkz}</td>
                        <td>{g.bereich}</td>
                        <td className="klein leise">{g.ort ?? ''}</td>
                        <td className="zahl">{g.auftraege}</td>
                        {darfMelden && (
                          <td className="knopfleiste">
                            <button className="knopf" disabled={laeuft}
                                    onClick={() => gebietWeg(g.onkz)}>
                              weg
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Karte>

            {/* DIE EIGENTLICHE ARBEITSLISTE DIESES REITERS. Solange hier
                etwas steht, fehlen Aufträge in JEDER Meldung, und ohne diese
                Liste sieht es niemand: sie fallen einfach aus jeder Zahl
                heraus. */}
            <Karte titel={`Kennzahlen an Aufträgen ohne Bereich `
                        + `(${d.ohne_bereich.length})`}>
              {!d.ohne_bereich.length ? (
                <Leer text="Jede Ortsnetzkennzahl, die an einem Auftrag steht,
                            gehört zu einem Bereich." />
              ) : (
                <>
                  <p className="klein leise" style={{ marginTop: 0 }}>
                    Diese Aufträge fallen aus jeder Kapazitätsmeldung heraus:
                    sie gehören zu keinem Gebiet, und gemeldet wird je Gebiet.
                  </p>
                  <table className="liste">
                    <thead>
                      <tr>
                        <th style={{ width: 110 }}>ONKz</th>
                        <th style={{ width: 200 }}>Ort</th>
                        <th className="zahl" style={{ width: 110 }}>Aufträge</th>
                        {darfMelden && <th></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {d.ohne_bereich.map((o: any) => (
                        <tr key={o.onkz} style={{ cursor: 'default' }}>
                          <td className="einfarbig">{o.onkz}</td>
                          <td className="klein leise">{o.ort ?? ''}</td>
                          <td className="zahl">{o.auftraege}</td>
                          {darfMelden && (
                            <td className="knopfleiste breit">
                              {d.bereiche.map((b: string) => (
                                <button key={b} className="knopf"
                                        disabled={laeuft}
                                        title={`${o.onkz} dem Bereich `
                                             + `„${b}" zuordnen`}
                                        onClick={() => gebietSetzen(
                                          o.onkz, b, o.ort ?? undefined)}>
                                  → {b}
                                </button>
                              ))}
                              {!d.bereiche.length && (
                                <span className="klein leise">
                                  Erst einen Bereich anlegen.
                                </span>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </Karte>
          </>
        )}
      </div>

      {/* EINE KOPIE ÜBERSCHREIBT NICHTS, WAS SCHON IM PORTAL STEHT. Das ist
          keine Nebensache und steht deshalb im Fenster, nicht in einer
          Fussnote: eine abgegebene Zusage still zu ersetzen hiesse, den
          Bildschirm von der Wahrheit im Portal zu trennen. */}
      {kopieren && (
        <Dialog titel={`${d.kommende_woche.text} auf weitere Wochen kopieren`}
                untertitel={bereich} breite={520} laeuft={laeuft}
                zu={() => setKopieren(false)}>
          <p className="klein">
            Kopiert werden die erfassten Zahlen des Bereichs
            {' '}<b>{bereich}</b> aus {d.kommende_woche.text} auf die
            folgenden Wochen. Eine Woche, die schon im Portal steht, wird
            nicht angefasst und danach aufgezählt.
          </p>
          <div className="feldpaar">
            <label htmlFor="kopie_wochen">Auf wie viele Wochen</label>
            <input id="kopie_wochen" type="number" min={1} max={26}
                   value={kopieWochen}
                   onChange={(e) => setKopieWochen(e.target.value)} />
          </div>
          <div className="reihe" style={{ marginTop: 14 }}>
            <button className="knopf haupt" disabled={laeuft} onClick={kopiere}>
              {laeuft ? 'Einen Moment' : 'Kopieren'}
            </button>
            <button className="knopf" disabled={laeuft}
                    onClick={() => setKopieren(false)}>
              Abbrechen
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}
export function Nachrichten() {
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => { api.nachrichten().then(setD).catch((f) => setFehler(f.message)); }, []);
  if (fehler) return <div className="inhalt"><div className="hinweis fehler">{fehler}</div></div>;
  if (!d) return null;

  return (
    <>
      <Kopf titel="Nachrichten" weg="Versandprotokoll aller Kanäle" />
      <div className="inhalt">

        <div className="nebeneinander">
          <Karte titel="Was wurde versendet">
            <table className="liste">
              <thead><tr><th>Anlass</th><th style={{ width: 90 }}>Kanal</th>
                <th className="zahl" style={{ width: 80 }}>Anzahl</th>
                <th style={{ width: 130 }}>zuletzt</th></tr></thead>
              <tbody>
                {d.anlaesse.map((a: any, i: number) => (
                  <tr key={i} style={{ cursor: 'default' }}>
                    <td>{a.anlass}</td>
                    <td className="klein leise">{a.kanal}</td>
                    <td className="zahl">{a.n}</td>
                    <td className="klein leise">{zeitpunkt(a.zuletzt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Karte>

          <Karte titel="Kundenmails, Stand je Auftrag">
            {d.kunden.length === 0 ? <Leer text="Keine Kundenmail-Stände." /> : (
              <table className="liste">
                <thead><tr><th>Status</th><th className="zahl" style={{ width: 90 }}>Aufträge</th></tr></thead>
                <tbody>
                  {d.kunden.map((k: any) => (
                    <tr key={k.status} style={{ cursor: 'default' }}>
                      <td>{k.status}</td><td className="zahl">{k.n}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Karte>
        </div>

        <Karte titel={`Protokoll (${d.zeilen.length} neueste)`}>
          <table className="liste">
            <thead>
              <tr><th style={{ width: 130 }}>Wann</th><th style={{ width: 90 }}>Kanal</th>
              <th style={{ width: 130 }}>Anlass</th><th>Empfänger</th>
              <th style={{ width: 200 }}>Betreff</th>
              {/* OB ES HINAUSGING, UND WENN NICHT, WARUM (23.09.2026).
                  Marvin: „der push nach telegram funktioniert nicht. dort
                  kam nichts an!" Der Grund stand im Protokoll, und diese
                  Tabelle zeigte ihn nicht. */}
              <th style={{ width: 260 }}>Ergebnis</th></tr>
            </thead>
            <tbody>
              {d.zeilen.slice(0, 60).map((z: any, i: number) => (
                <tr key={i} style={{ cursor: 'default' }}>
                  <td className="klein leise">{zeitpunkt(z.gesendet_am)}</td>
                  <td className="klein">{z.kanal}</td>
                  <td className="klein">{z.anlass ?? ''}</td>
                  <td className="klein leise" style={{ wordBreak: 'break-all' }}>
                    {(z.empfaenger ?? '').slice(0, 60)}
                  </td>
                  <td className="klein">{(z.betreff ?? '').slice(0, 50)}</td>
                  <td className="klein">
                    {z.erfolgreich
                      ? <>
                          <span className="marke-zustand z-erledigt">gesendet</span>
                          {z.ergebnis && <div className="leise">{String(z.ergebnis).slice(0, 200)}</div>}
                        </>
                      : <>
                          <span className="marke-zustand z-ueberfaellig">nicht gesendet</span>
                          {z.ergebnis && <div className="leise">{String(z.ergebnis).slice(0, 200)}</div>}
                        </>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Karte>
      </div>
    </>
  );
}


/* ============================================================================
 * LEISTUNGSPOSITIONEN: MENGE UND BAUSTELLEN, GEGLIEDERT (.230)
 * ============================================================================
 * ORIGINALTEXT MARVIN (25.09.2026)
 *   „die mengen mit positionen kommt in die auswertung rein. ... die zeilen
 *    können da raus. stattdessen muss ich diese infos, dann auf orte, onkz
 *    usw auswerten können. und auch in welcher anzahl das bei baustellen
 *    welche position war."
 * ==========================================================================*/
const GLIEDERUNG: [string, string][] = [
  ['gesamt', 'Gesamt'], ['ort', 'je Ort'], ['onkz', 'je ONKZ'],
  ['gemeinde', 'je Gemeinde'], ['firma', 'je Firma'], ['monat', 'je Monat'],
];
function Leistungen({ von, bis }: { von: string; bis: string }) {
  const [nach, setNach] = useState('gesamt');
  const [d, setD] = useState<any>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!von || !bis) return;
    let weg = false;
    api.auswertungLeistungen(von, bis, nach)
      .then((x) => { if (!weg) { setD(x); setFehler(null); } })
      .catch((f) => { if (!weg) setFehler(String(f?.message ?? f)); });
    return () => { weg = true; };
  }, [von, bis, nach]);

  const gruppen: any[] = d?.gruppen ?? [];
  const zeilenJe = new Map<string, any[]>();
  for (const z of d?.zeilen ?? []) (zeilenJe.get(z.gruppe) ?? zeilenJe.set(z.gruppe, []).get(z.gruppe)!).push(z);
  const zahlDe = (n: any, stellen = 1) =>
    Number(n ?? 0).toLocaleString('de-DE', { maximumFractionDigits: stellen });

  return (
    <Karte titel="Leistungspositionen" rechts={<Sorte art="zeitraum" />}>
      <div className="knopfreihe" style={{ marginBottom: 10 }}>
        {GLIEDERUNG.map(([w, t]) => (
          <button key={w} className={'knopf klein' + (nach === w ? ' gewaehlt' : '')}
                  onClick={() => { setNach(w); setOffen({}); }}>{t}</button>
        ))}
      </div>
      {fehler && <div className="hinweis fehler">{fehler}</div>}
      {d && !gruppen.length && <Leer text="In diesem Zeitraum ist kein Aufmaß erfasst." />}
      {gruppen.map((g: any, i: number) => {
        const zeilen = zeilenJe.get(g.gruppe) ?? [];
        const auf = nach === 'gesamt' || offen[g.gruppe] || (i === 0 && offen[g.gruppe] === undefined);
        return (
          <div key={g.gruppe} className="leistungsgruppe">
            {nach !== 'gesamt' && (
              <button type="button" className="leistungskopf"
                      onClick={() => setOffen({ ...offen, [g.gruppe]: !auf })}>
                <span className="pfeil" aria-hidden="true">{auf ? '▾' : '▸'}</span>
                <b>{g.gruppe}</b>
                <span className="klein leise">{g.baustellen} {g.baustellen === 1 ? 'Baustelle' : 'Baustellen'}, {zeilen.length} Positionen</span>
              </button>
            )}
            {auf && (
              <table className="liste">
                <thead>
                  <tr>
                    <th style={{ width: 100 }}>OZ</th><th>Leistung</th>
                    <th className="zahl" style={{ width: 110 }}>Menge</th>
                    <th className="zahl" style={{ width: 150 }}>bei Baustellen</th>
                    <th className="zahl" style={{ width: 120 }}>je Baustelle</th>
                  </tr>
                </thead>
                <tbody>
                  {zeilen.map((z: any) => (
                    <tr key={z.oz} style={{ cursor: 'default' }}>
                      <td className="klein">{z.oz}</td>
                      <td>{z.text}</td>
                      <td className="zahl">{menge(z.menge, z.einheit)}</td>
                      <td className="zahl">
                        {z.baustellen}
                        <span className="klein leise"> von {g.baustellen} ({Math.round(100 * z.baustellen / Math.max(1, g.baustellen))} %)</span>
                      </td>
                      <td className="zahl leise">{zahlDe(Number(z.menge) / Math.max(1, z.baustellen), 2)} {z.einheit ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}
      <p className="klein leise" style={{ marginBottom: 0, marginTop: 10 }}>
        Menge: geprüft, sonst gemeldet. „bei Baustellen": an wie vielen Aufträgen die Position im
        Aufmaß steht, von allen mit Aufmaß in dieser Gruppe. Zeitraum: Arbeitsdatum des Auftrags.
        Ohne Preise, wie überall.
      </p>
    </Karte>
  );
}
